import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Button,
  Badge,
  Alert,
  Modal,
  Form,
  Row,
  Col,
  Card,
  ProgressBar,
  Nav,
  Modal as BootstrapModal,
  Tooltip,
  OverlayTrigger,
  Tabs,
  Tab,
  InputGroup,
  Collapse,
  Spinner,
} from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';
import {
  // createQuestionnaire,
  // getQuestionnaires,
  // updateQuestionnaire,
  deleteQuestionnaire,
  getQuestionnaireById,
  getOrganizationActiveGrades,
  getActiveGradesLegacy,
  getUserRoles,
  getOrganizationSettings,
  updateOrganizationSettings,
  createQuestionnaireRaw,
  getQuestionnairesByOrganization,
  updateQuestionnaireRaw,
  ORG_ID_CHANGE_EVENT,
  hasBaseGrades,
} from "../services/api";

import { Toast, ToastContainer } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

const REMCHANNEL_COLORS = {
  steelBlue: "#003366",
  green: "#00A651",
  black: "#000000",
  paper: "#FFFFFF",
  orange: "#FF6B00",
  rust: "#C35214",
  blue: "#1B3664",
  grey: "#666666",
  // Tints/Shades
  steelBlue80: "#33587A",
  steelBlue60: "#668BAF",
  steelBlue40: "#99BFD4",
  steelBlue20: "#CCDFF0",
  green80: "#33B86B",
  green60: "#66CB8C",
  green40: "#99DEAD",
  green20: "#CCF1CE",
  orange80: "#FF8A33",
  orange60: "#FFA866",
  orange40: "#FFC699",
  orange20: "#FFE4CC",
  rust80: "#D16B33",
  rust60: "#E18566",
  rust40: "#F1A099",
  rust20: "#F9CABB",
  blue80: "#495A7A",
  blue60: "#7A8BAF",
  blue40: "#AABFD4",
  blue20: "#D5DFF0",
  grey80: "#888888",
  grey60: "#AAAAAA",
  grey40: "#CCCCCC",
  grey20: "#EEEEEE",
};

const THEME = {
  // Primary greens
  primary: "#00A651", // Main green
  primaryDark: "#008c44", // Darker green for hover states
  primaryDarker: "#006633", // Even darker green for active states
  primaryLight: "#e8f5e9", // Light green for backgrounds
  primaryLighter: "#f1f8e9", // Even lighter green for subtle backgrounds
  primaryBorder: "#4caf50", // Green for borders

  // Grey tones
  grey900: "#212529", // Darkest grey for primary text
  grey800: "#343a40", // Dark grey for secondary text
  grey700: "#495057", // Grey for headings
  grey600: "#6c757d", // Grey for secondary text
  grey500: "#adb5bd", // Grey for borders
  grey400: "#ced4da", // Light grey for disabled states
  grey300: "#dee2e6", // Lighter grey for borders
  grey200: "#e9ecef", // Very light grey for backgrounds
  grey100: "#f8f9fa", // Lightest grey for backgrounds

  // Status colors
  success: "#00A651", // Success green
  warning: "#ffc107", // Warning yellow
  danger: "#dc3545", // Danger red
  info: "#17a2b8", // Info blue

  // Base colors
  background: "#ffffff",
  backgroundAlt: "#f8f9fa",
  border: "#dee2e6",
  shadow: "rgba(0, 0, 0, 0.1)",
};

// Add global styles
const GLOBAL_STYLES = {
  card: {
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${THEME.grey300}`,
    borderRadius: "8px",
  },
  button: {
    primary: {
      backgroundColor: THEME.primary,
      borderColor: THEME.primary,
      color: "#fff",
      "&:hover": {
        backgroundColor: THEME.primaryDark,
        borderColor: THEME.primaryDark,
      },
    },
    secondary: {
      backgroundColor: THEME.grey200,
      borderColor: THEME.grey300,
      color: THEME.grey800,
      "&:hover": {
        backgroundColor: THEME.grey300,
        borderColor: THEME.grey400,
      },
    },
  },
  input: {
    border: `1px solid ${THEME.grey300}`,
    borderRadius: "4px",
    "&:focus": {
      borderColor: THEME.primary,
      boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
    },
  },
};

// Constants for question options
const QUESTIONS = {
  LTI_ELIGIBILITY: {
    id: 1,
    title: "LTI Eligibility",
    description: "Please indicate the eligibility to participate in the long-term incentive plan(s) for each of the grades.",
    display: true,
    displayOrder: 1,
    options: {
      none: "None",
      some: "Some",
      all: "All",
    },
  },
  LTI_TARGET: {
    id: 2,
    title: "LTI Target Annual Awards",
    description: "Indicate the target annual long-term incentive awards by grade, expressed as percentages of the applicable remuneration type provided. If awards are not based on percentages of any remuneration type, please specify the detail.",
    display: false,
    displayOrder: 2,
  },
  LTI_ALLOCATION: {
    id: 3,
    title: "LTI Allocation Methodology",
    description: "Indicate the methodology used when making LTI allocations",
    display: false,
    displayOrder: 3,
    options: {
      percentage_grp: "Percentage of individual Guaranteed Remuneration Package (GRP) or Total Remuneration (TR)",
      percentage_band: "Percentage of average for grade or band",
      targeted_value: "Targeted value for grade",
      other: "Other criteria",
    },
  },
  LTI_PLANS: {
    id: 4,
    title: "LTI Plan Types",
    description: "Select types of LTI plans that apply to each grade.",
    display: true,
    displayOrder: 3,
    options: {
      stock_options: "Stock/Share Options",
      sars: "Share Appreciation Rights (SARs)",
      fsp: "Forfeitable Share Plan (FSP)",
      conditional_share: "Conditional Share Plan",
      single_incentive: "Single Incentive Plan",
      value_appreciation: "Value Appreciation Rights",
      deferred_bonus: "Deferred Bonus Plan",
      other: "Other",
    },
  },
  LTI_PERFORMANCE_CONDITIONS: {
    id: 5,
    title: "LTI Performance Conditions",
    description: "If participation is subject to performance conditions, please indicate the conditions that apply.",
    display: true,
    displayOrder: 4,
    options: {
      shareholder_value: "Shareholder value",
      earnings_measure: "Earnings Measure",
      returns_measure: "Returns measure",
      profitability_measures: "Profitability measures",
      strategic_measures: "Strategic Measures",
      esg_measures: "ESG measures",
      cash_flow_measures: "Cash flow measures",
      individual_measures: "Individual measures",
      operational_measures: "Operational measures",
      balance_sheet_measures: "Balance sheet measures",
      cost_management: "Cost Management",
      diversity_and_inclusion: "Diversity and Inclusion",
      revenue_measures: "Revenue Measures",
      customer_centric: "Customer Centric",
      safety_measures: "Safety Measures",
      other: "Other",
    },
  },
  LTI_VESTING_PERCENTAGES: {
    id: 6,
    title: "LTI Vesting Percentages",
    description: "Please indicate the performance-related vesting percentages applicable.",
    display: false,
    displayOrder: 5,
  },
  LTI_VESTING_METHODOLOGY: {
    id: 7,
    title: "LTI Vesting Methodology",
    description: "What type(s) of vesting methodology is utilised in your organisation? (Hold CTRL to select multiple)",
    display: true,
    displayOrder: 5,
    options: {
      cliff_vesting: "Cliff Vesting (All at once)",
      tranche_vesting: "Tranche (Staggered over time)",
      other: "Other",
    },
  },
  LTI_VESTING_PERIOD: {
    id: 8,
    title: "LTI Vesting Period",
    description: "Please indicate the vesting period by Grade and Scheme type below.",
    display: true,
    displayOrder: 6,
    options: {
      annually: "Annually",
      two_years: "2 years",
      three_years: "3 years",
      four_years: "4 years",
      five_years: "5 years",
      longer_than_5_years: "Longer than 5 years",
    },
  },
  LTI_SETTLEMENT_METHOD: {
    id: 9,
    title: "LTI Settlement Methodology",
    description: "Please choose the manner in which settlements typically occur, even if your policy caters for multiple settlement options.",
    display: true,
    displayOrder: 7,
    options: {
      equity: "Equity",
      cash: "Cash",
      digital_currency: "Digital currency (Crypto, Bitcoin, Stable coin)",
      combination: "Combination",
      other: "Other",
    },
  },
  STI_ELIGIBILITY: {
    id: 10,
    title: "STI Eligibility",
    description: "Who is eligible to participate in the short-term incentive plan?",
    display: true,
    displayOrder: 8,
    options: {
      None: "None",
      Some: "Some",
      All: "All",
    },
  },
  STI_ALLOCATION: {
    id: 11,
    title: "STI Allocation Methodology",
    description: "Does your policy provide for on-target allocations or maximum allocations?",
    display: false,
    displayOrder: 11,
    options: {
      percentage_tgp: "Percentage of Total guaranteed package",
      percentage_base: "Percentage of Base salary",
      monetary_value: "Monetary value by grade",
      other: "Other",
    },
  },  
  STI_REWARD_PERCENTAGES: {
    id: 12,
    title: "STI Reward Percentages",
    description: "Indicate the reward percentages for each level of performance.",
    display: false,
    displayOrder: 11,
   
  },
  STI_PLAN_TYPE: {
    id: 13,
    title: "STI Plan Type",
    description: "Indicate the type(s) of short-term incentive plan(s) in your organisation. (Hold CTRL to select multiple)",
    display: false,
    displayOrder: 16,
    options: {
      weighted_formula: "Weighted formula method",
      multiplicative_formula: "Multiplicative formula method",
      bonus_pool_performance: "Bonus pool (determined in excess of a specific performance hurdle)",
      bonus_pool_earnings: "Bonus pool (earnings sharing)",
      hybrid: "Hybrid approach",
      single_incentive: "Single incentive",
      other: "Other",
    },
  },
  STI_PERFORMANCE_ASSESSMENT: {
    id: 14,
    title: "STI Performance measurement criteria",
    description: "How are performance conditions weighted at the different grades?",
    display: true,
    displayOrder: 11,
  },
  STI_PERFORMANCE_CONDITIONS: {
    id: 15,
    title: "STI Performance conditions",
    description: "If participation is subject to performance conditions, please indicate the conditions that apply (Hold CTRL to select multiple).",
    display: true,
    displayOrder: 12,
    options: {
      profitability_measures: "Profitability Measures",
      individual_measures: "Individual Measures",
      strategic_measures: "Strategic Measures",
      esg_measures: "ESG Measures",
      returns_measures: "Returns Measures",
      cost_management: "Cost Management",
      operational_measures: "Operational Measures",
      cash_flow_measures: "Cash Flow Measures",
      revenue_measures: "Revenue Measures",
      shareholder_value: "Shareholder Value",
      safety_measures: "Safety Measures",
      balance_sheet: "Balance Sheet",
      customer_centric: "Customer Centric",
      risk_management: "Risk Management",
      other: "Other",
    },
  },
  STI_PERFORMANCE_MODIFIERS: {
    id: 16,
    title: "STI Performance Modifiers",
    description: "Indicate the performance modifiers applicable for each level of performance (Hold CTRL to select multiple).",
    display: true,
    displayOrder: 13,
    options: {
      profitability_measures: "Profitability Measures",
      individual_measures: "Individual Measures",
      strategic_measures: "Strategic Measures",
      esg_measures: "ESG Measures",
      returns_measures: "Returns Measures",
      cost_management: "Cost Management",
      operational_measures: "Operational Measures",
      cash_flow_measures: "Cash Flow Measures",
      revenue_measures: "Revenue Measures",
      shareholder_value: "Shareholder Value",
      safety_measures: "Safety Measures",
      balance_sheet: "Balance Sheet",
      customer_centric: "Customer Centric",
      risk_management: "Risk Management",
      other: "Other",
    },
  },

  LTI_TARGET_ANNUAL_AWARDS: {
    id: 17,
    title: "LTI Annual Awards",
    description: "On which remuneration base are the LTI awards based?",
    display: true,
    displayOrder: 2,
    options: {
      percentage_base: "Percentage of Base Pay",
      percentage_tgp: "Percentage of Total Guaranteed Package", 
      monetary_value: "Monetary value by grade",
      other: "Other",
    },
    options_awards: {
      threshold: "Threshold",
      target: "Target",
      stretch: "Stretch",
    },
  },
  STI_TARGET_ANNUAL_AWARDS: {
    id: 18,
    title: "STI Annual Awards",
    description: "On which remuneration base are the STI awards based?",
    display: true,
    displayOrder: 9,
    options: {
      percentage_base: "Percentage of Base Pay",
      percentage_tgp: "Percentage of Total Guaranteed Package", 
      monetary_value: "Monetary value by grade",
      other: "Other",
    },
    options_awards: {
      threshold: "Threshold",
      target: "Target",
      stretch: "Stretch",
    },
  },
  STI_PLAN_TYPE_V2: {
    id: 19,
    title: "STI Plan Types",
    description: "Indicate the values for each type of short-term incentive plan.",
    display: true,
    displayOrder: 10,
    options: {
      weighted_formula: "Weighted formula method",
      multiplicative_formula: "Multiplicative formula method",
      bonus_pool_performance: "Bonus pool (determined in excess of a specific performance hurdle)",
      bonus_pool_earnings: "Bonus pool (earnings sharing)",
      hybrid: "Hybrid approach",
      single_incentive: "Single incentive",
      other: "Other",
    },
  },

};

// Remove date-fns import and add formatDate helper function
const formatDate = (date) => {
  const d = new Date(date);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = d.getDate().toString().padStart(2, "0");
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

const renderPercentageDropdown = (
  value,
  onChange,
  includeNoSelection = true
) => (
  <Form.Control
    type="number"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    required={true}
    style={{ fontSize: "0.8rem" }}
    min="0"
    max="100"
    step="0.01"
    placeholder={includeNoSelection ? "Enter percentage..." : "0"}
  />
);

const renderPercentage100Dropdown = (
  value,
  onChange,
  includeNoSelection = true
) => (
  <Form.Select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    required={true}
    style={{ fontSize: "0.8rem" }}
  >
    {includeNoSelection && <option value="">No Selection</option>}
    {Array.from({ length: 21 }, (_, i) => i * 5).map((value) => (
      <option key={value} value={value}>
        {value}%
      </option>
    ))}
  </Form.Select>
);

const QUESTION_NUMBERS = Object.values(QUESTIONS).filter(q => q.display).sort((a, b) => a.displayOrder - b.displayOrder).map(q => q.displayOrder);


const InfoBox = ({ children }) => (
  <div
    className="info-box mb-3"
    style={{
      backgroundColor: THEME.primaryLighter,
      border: `1px solid ${THEME.primaryBorder}`,
      borderRadius: "4px",
      padding: "1rem",
      color: THEME.grey800,
    }}
  >
    <i className="bi bi-info-circle me-2" style={{ color: THEME.primary }}></i>
    {children}
  </div>
);

// Reusable DataTable Component with sorting, filtering, and pagination
const DataTable = ({ 
  title, 
  data, 
  columns, 
  defaultOpen = true,
  searchable = true,
  pageSize = 10,
  collapsible = true
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(row => 
      columns.some(col => {
        const value = col.accessor ? col.accessor(row) : row[col.key];
        return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = columns.find(c => c.key === sortConfig.key)?.accessor 
        ? columns.find(c => c.key === sortConfig.key).accessor(a)
        : a[sortConfig.key];
      const bValue = columns.find(c => c.key === sortConfig.key)?.accessor
        ? columns.find(c => c.key === sortConfig.key).accessor(b)
        : b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, columns]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" /> 
      : <FaSortDown className="ms-1" />;
  };

  const tableContent = (
    <Card.Body>
      {searchable && (
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </InputGroup>
      )}
      
      <div className="table-responsive">
        <Table hover striped bordered>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    fontSize: '12px'
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  {col.header}
                  {getSortIcon(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => (
                <tr key={row.id || row.questionnaireId || index}>
                  {columns.map(col => (
                    <td 
                      key={col.key}
                      style={{ fontSize: '12px' }}
                    >
                      {col.render 
                        ? col.render(row[col.key], row)
                        : (col.accessor ? col.accessor(row) : row[col.key] ?? '-')
                      }
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td 
                  colSpan={columns.length} 
                  className="text-center"
                  style={{ fontSize: '12px' }}
                >
                  {searchTerm ? 'No results found' : 'No data available'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
          </div>
          <div>
            <Button
              variant="outline-success"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="me-2"
            >
              Previous
            </Button>
            <span className="mx-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline-success"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card.Body>
  );

  if (!collapsible) {
    return (
      <Card className="mb-4">
        {tableContent}
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header 
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(!open)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{title}</h5>
          <span>
            {open ? <FaChevronUp /> : <FaChevronDown />}
          </span>
        </div>
      </Card.Header>
      <Collapse in={open}>
        <div>
          {tableContent}
        </div>
      </Collapse>
    </Card>
  );
};

// Utility function to convert snake_case to sentence case
const snakeCaseToSentenceCase = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Keys in answer.value that are not grades - show business-friendly labels in the view modal
const ANSWER_ROW_LABELS = {
  value_b: 'Performance conditions by plan type',
};
const getAnswerRowLabel = (key) => ANSWER_ROW_LABELS[key] || key;

// Utility function to format JSON values for display (returns plain value for use in text)
const formatJsonValue = (value, questionOptions = null) => {
  if (value === null || value === undefined) return value;
  
  // If it's a string, try to convert snake_case to sentence case
  if (typeof value === 'string') {
    // Check if it looks like snake_case (contains underscores and lowercase)
    if (value.includes('_') && value === value.toLowerCase()) {
      return snakeCaseToSentenceCase(value);
    }
    // Check if there's a matching option label
    if (questionOptions && questionOptions[value]) {
      return questionOptions[value];
    }
    return value;
  }
  
  // If it's an array, format each item
  if (Array.isArray(value)) {
    return value.map(item => formatJsonValue(item, questionOptions));
  }
  
  // If it's an object, recursively format values
  if (typeof value === 'object') {
    const formatted = {};
    for (const [key, val] of Object.entries(value)) {
      formatted[key] = formatJsonValue(val, questionOptions);
    }
    return formatted;
  }
  
  return value;
};

// Business-friendly UI for answer values (no raw JSON)
const FriendlyAnswerDisplay = ({ value, questionOptions }) => {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;

  // Primitive: show as badge
  if (typeof value !== 'object') {
    const display = typeof value === 'string' && value.includes('_') && value === value.toLowerCase()
      ? snakeCaseToSentenceCase(value)
      : (questionOptions && questionOptions[value]) || value;
    return (
      <Badge bg="light" text="dark" className="me-1 mb-1">
        {String(display)}
      </Badge>
    );
  }

  // Array: show as list of badges or list
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted">None</span>;
    return (
      <ul className="list-unstyled mb-0 d-flex flex-wrap gap-1">
        {value.map((item, idx) => (
          <li key={idx} className="d-inline">
            {typeof item === 'object' && item !== null ? (
              <FriendlyAnswerDisplay value={item} questionOptions={questionOptions} />
            ) : (
              <Badge bg="light" text="dark" className="me-1">
                {String(questionOptions && questionOptions[item] ? questionOptions[item] : (typeof item === 'string' && item.includes('_') ? snakeCaseToSentenceCase(item) : item))}
              </Badge>
            )}
          </li>
        ))}
      </ul>
    );
  }

  // Object: show as key-value rows (readable labels)
  const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return <span className="text-muted">—</span>;

  return (
    <div className="small">
      {entries.map(([key, val]) => (
        <div key={key} className="mb-2">
          <div className="text-muted fw-medium mb-1" style={{ fontSize: '0.75rem' }}>
            {snakeCaseToSentenceCase(key)}
          </div>
          <div>
            {typeof val === 'object' && val !== null ? (
              <FriendlyAnswerDisplay value={val} questionOptions={questionOptions} />
            ) : (
              <Badge bg="light" text="dark">
                {String(typeof val === 'string' && val.includes('_') && val === val.toLowerCase() ? snakeCaseToSentenceCase(val) : (questionOptions && questionOptions[val] ? questionOptions[val] : val))}
              </Badge>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const GradeTable = ({ grades, renderCell }) => (
  <Table bordered className="grade-table">
    <thead>
      <tr>
        <th style={{ width: "200px" }}>Grade</th>
        <th>Response</th>
      </tr>
    </thead>
    <tbody>
      {grades.map((grade) => (
        <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
          <td>{grade.code}</td>
          <td>{renderCell(grade)}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const renderLTIEligibility = (grade, value, onChange) => (
  <Form.Select
    value={value || ""}
    onChange={(e) => onChange(grade.code, e.target.value)}
    required={true}
    style={{ fontSize: "0.8rem" }}
  >
    <option value="">Select...</option>
    {Object.entries(QUESTIONS.LTI_ELIGIBILITY.options).map(([value, label]) => (
      <option key={value} value={value}>{label}</option>
    ))}
  </Form.Select>
);

const renderSTIEligibility = (grade, value, onChange) => (
  <Form.Select
    value={value || ""}
    onChange={(e) => onChange(grade.code, e.target.value)}
    required={true}
    style={{ fontSize: "0.8rem" }}
  >
    <option value="">Select...</option>
    {Object.entries(QUESTIONS.STI_ELIGIBILITY.options).map(([value, label]) => (
      <option key={value} value={value}>{label}</option>
    ))}
  </Form.Select>
);

const renderAllocationMethodology = (value, onChange) => (
  <Form.Select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    required={true}
    style={{ fontSize: "0.8rem" }}
  >
    <option value="">Select methodology</option>
    {Object.entries(QUESTIONS.LTI_ALLOCATION.options).map(([value, label]) => (
      <option key={value} value={value}>{label}</option>
    ))}
  </Form.Select>
);

const renderQuestion = (questionNumber, data, answers, handleAnswerChange, cleanupDeselectedPlans) => {
  const questionList = Object.values(QUESTIONS)
    .filter(q => q.display !== false)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  
  const currentQuestionData = questionList[questionNumber - 1];
  if (!currentQuestionData) {
    return null;
  }
  
  switch (currentQuestionData.id) {
    case 1:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_ELIGIBILITY.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_ELIGIBILITY.description}
          </InfoBox>
          <GradeTable
            grades={data.grades}
            renderCell={(grade) =>
              renderLTIEligibility(
                grade,
                answers[1]?.value?.[grade.code],
                (gradeCode, value) =>
                  handleAnswerChange(1, {
                    ...answers[1]?.value,
                    [gradeCode]: value,
                  })
              )
            }
          />
        </div>
      );

    case 2:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_TARGET.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_TARGET.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                <th>Base Pay</th>
                <th>TGP</th>
                <th>Other</th>
              </tr>
            </thead>
            <tbody>
              {data.grades
                .filter((grade) => {
                  const eligibility = answers[1]?.value?.[grade.code];
                  return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                })
                .map((grade) => (
                  <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                    <td>{grade.code}</td>
                    <td>
                      {renderPercentageDropdown(
                        answers[2]?.value?.[grade.code]?.basePay,
                        (value) =>
                          handleAnswerChange(2, {
                            ...answers[2]?.value,
                            [grade.code]: {
                              ...answers[2]?.value?.[grade.code],
                              basePay: value,
                            },
                          })
                      )}
                    </td>
                    <td>
                      {renderPercentageDropdown(
                        answers[2]?.value?.[grade.code]?.tgp,
                        (value) =>
                          handleAnswerChange(2, {
                            ...answers[2]?.value,
                            [grade.code]: {
                              ...answers[2]?.value?.[grade.code],
                              tgp: value,
                            },
                          })
                      )}
                    </td>
                    <td>
                      {renderPercentageDropdown(
                        answers[2]?.value?.[grade.code]?.other,
                        (value) =>
                          handleAnswerChange(2, {
                            ...answers[2]?.value,
                            [grade.code]: {
                              ...answers[2]?.value?.[grade.code],
                              other: value,
                            },
                          })
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      );

    case 3:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_ALLOCATION.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_ALLOCATION.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                <th>Allocation Methodology</th>
                <th>Other Details</th>
              </tr>
            </thead>
            <tbody>
              {data.grades
                .filter((grade) => {
                  const eligibility = answers[1]?.value?.[grade.code];
                  return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                })
                .map((grade) => (
                  <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                    <td>{grade.code}</td>
                    <td>
                      {renderAllocationMethodology(
                        answers[3]?.value?.[grade.code]?.methodology,
                        (value) =>
                          handleAnswerChange(3, {
                            ...answers[3]?.value,
                            [grade.code]: {
                              ...(answers[3]?.value?.[grade.code] || {}),
                              methodology: value,
                            },
                          })
                      )}
                    </td>
                    <td>
                      {answers[3]?.value?.[grade.code]?.methodology ===
                        "other" && (
                        <Form.Control
                          as="textarea"
                          required={true}
                          rows={2}
                          value={
                            answers[3]?.value?.[grade.code]?.otherDetails || ""
                          }
                          onChange={(e) =>
                            handleAnswerChange(3, {
                              ...answers[3]?.value,
                              [grade.code]: {
                                ...(answers[3]?.value?.[grade.code] || {}),
                                otherDetails: e.target.value,
                              },
                            })
                          }
                          placeholder="Please specify other criteria"
                        />
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      );

    case 4:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_PLANS.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_PLANS.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                {Object.entries(QUESTIONS?.LTI_PLANS?.options || {}).map(([value, label]) => (
                  <th key={value}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.grades
                .filter((grade) => {
                  const eligibility = answers[1]?.value?.[grade.code];
                  return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                })
                .map((grade) => (
                  <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                    <td>{grade.code}</td>
                    {Object.entries(QUESTIONS?.LTI_PLANS?.options || {}).map(([value, label]) => (
                        <td key={value} className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={
                            (Array.isArray(answers[4]?.value?.[grade.code]) &&
                              answers[4]?.value?.[grade.code]?.includes(
                                value
                              )) ||
                            false
                          }
                          onChange={(e) => {
                            const currentValues = Array.isArray(
                              answers[4]?.value?.[grade.code]
                            )
                              ? answers[4]?.value?.[grade.code]
                              : [];
                            const newValues = e.target.checked
                              ? [...currentValues, value]
                              : currentValues.filter((v) => v !== value);
                            handleAnswerChange(4, {
                              ...answers[4]?.value,
                              [grade.code]: newValues,
                            });
                            cleanupDeselectedPlans({
                              ...answers[4]?.value,
                              [grade.code]: newValues,
                            });
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>

          <h6 className="mt-4">LTI Performance Conditions</h6>
          <InfoBox>
            Indicate which LTI plans are subject to performance conditions
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>LTI Plan</th>
                <th>Subject to performance conditions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Get all selected plans across all grades
                const selectedPlans = new Set();
                Object.values(answers[4]?.value || {}).forEach(
                  (gradeValues) => {
                    if (Array.isArray(gradeValues)) {
                      gradeValues.forEach((plan) => selectedPlans.add(plan));
                    }
                  }
                );

                // Map of plan codes to display names
                const planDisplayNames = QUESTIONS?.LTI_PLANS?.options || {};

                // Only show rows for selected plans
                return Array.from(selectedPlans).map((planCode) => (
                  <tr key={planCode}>
                    <td>{planDisplayNames[planCode] || planCode}</td>
                    <td>
                      <Form.Select
                        required={true}
                        value={
                          (answers[4]?.value?.value_b &&
                            answers[4]?.value?.value_b[planCode]) ||
                          ""
                        }
                        onChange={(e) => {
                          const currentValue = answers[4]?.value || {};
                          const currentValueB = currentValue.value_b || {};

                          handleAnswerChange(4, {
                            ...currentValue,
                            value_b: {
                              ...currentValueB,
                              [planCode]: e.target.value,
                            },
                          });
                        }}
                      >
                        <option value="">Select...</option>
                        <option value="performance_conditions_apply">
                          Performance conditions apply
                        </option>
                        <option value="no_performance_conditions">
                          No performance conditions apply
                        </option>
                      </Form.Select>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </Table>

          {Object.values(answers[4]?.value || {}).some(
            (values) => Array.isArray(values) && values.includes("other")
          ) && (
            <Form.Group className="mt-3">
              <Form.Label>Please specify other LTI plan types:</Form.Label>
              <Form.Control
                as="textarea"
                required={true}
                rows={3}
                value={answers[4]?.value?.otherDetails || ""}
                onChange={(e) =>
                  handleAnswerChange(4, {
                    ...answers[4]?.value,
                    otherDetails: e.target.value,
                  })
                }
              />
            </Form.Group>
          )}
        </div>
      );

    case 5:
      const getPlansWithPerformanceConditions = () => {
        const previousAnswers = answers[4]?.value || {};
        const selectedPlans = new Set();

        const planDisplayNames = QUESTIONS?.LTI_PLANS?.options || {};

        Object.entries(previousAnswers.value_b || {}).forEach(
          ([planCode, condition]) => {
            if (condition === "performance_conditions_apply") {
              selectedPlans.add(planCode);
            }
          }
        );

        return Array.from(selectedPlans).map((plan) => ({
          code: plan,
          name: planDisplayNames[plan] || plan,
        }));
      };

      const plansWithConditions = getPlansWithPerformanceConditions();

      // If no plans with performance conditions, show a message
      if (plansWithConditions.length === 0) {
        return (
          <div className="question-content">
            <h5>{QUESTIONS.LTI_PERFORMANCE_CONDITIONS.title}</h5>
            <InfoBox>
              {QUESTIONS.LTI_PERFORMANCE_CONDITIONS.description}
            </InfoBox>
            <Alert
              variant="danger"
            >
              No plans with performance conditions were selected in the previous
              question. Please go back if applicable to select plans with
              performance conditions.
            </Alert>
          </div>
        );
      }

      const performanceConditions = Object.values(QUESTIONS.LTI_PERFORMANCE_CONDITIONS.options);

      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_PERFORMANCE_CONDITIONS.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_PERFORMANCE_CONDITIONS.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Plan Type</th>
                <th>Performance Conditions (Hold CTRL to select multiple)</th>
              </tr>
            </thead>
            <tbody>
              {plansWithConditions.map((plan) => (
                <tr key={plan.code}>
                  <td>{plan.name}</td>
                  <td>
                    <div>
                      <Form.Select
                        multiple
                        required={true}
                        value={answers[5]?.value?.[plan.code] || []}
                        onChange={(e) => {
                          const selected = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value
                          );
                          handleAnswerChange(5, {
                            ...answers[5]?.value,
                            [plan.code]: selected,
                          });
                        }}
                        style={{
                          border: `1px solid ${THEME.grey300}`,
                          "&:focus": {
                            borderColor: THEME.primary,
                            boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
                          },
                        }}
                      >
                        {performanceConditions.map((condition) => (
                          <option key={condition} value={condition}>
                            {condition}
                          </option>
                        ))}
                      </Form.Select>
                      {answers[5]?.value?.[plan.code]?.includes("Other") && (
                        <Form.Control
                          as="textarea"
                          required={true}
                          rows={2}
                          value={answers[5]?.value?.[`${plan.code}_other`] || ""}
                          onChange={(e) =>
                            handleAnswerChange(5, {
                              ...answers[5]?.value,
                              [`${plan.code}_other`]: e.target.value,
                            })
                          }
                          placeholder="Please specify other performance conditions"
                          className="mt-2"
                          style={{
                            border: `1px solid ${THEME.grey300}`,
                            "&:focus": {
                              borderColor: THEME.primary,
                              boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
                            },
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );

    case 6:
      // Get the selected plans from the LTI plan selection question
      const getSelectedPlans = () => {
        const previousAnswers = answers[4]?.value || {};
        const selectedPlans = new Set();

        // Map of plan codes to display names
        const planDisplayNames = QUESTIONS?.LTI_PLANS?.options || {};

        // For each grade, collect selected plans
        Object.values(previousAnswers).forEach((gradePlans) => {
          if (Array.isArray(gradePlans)) {
            gradePlans.forEach((plan) => selectedPlans.add(plan));
          }
        });

        return Array.from(selectedPlans).map((plan) => ({
          code: plan,
          name: planDisplayNames[plan] || plan,
        }));
      };

      const selectedPlans = getSelectedPlans();

      // If no plans were selected, show a message
      if (selectedPlans.length === 0) {
        return (
          <div className="question-content">
            <h5>{QUESTIONS.LTI_VESTING_PERCENTAGES.title}</h5>
            <InfoBox>
              {QUESTIONS.LTI_VESTING_PERCENTAGES.description}
            </InfoBox>
            <Alert
              variant="danger"
            >
              No plans were selected in the previous question. Please go back if
              applicable to select the plans you want to specify vesting
              percentages for.
            </Alert>
          </div>
        );
      }

      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_VESTING_PERCENTAGES.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_VESTING_PERCENTAGES.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Plan Type</th>
                <th>Performance Level</th>
              </tr>
            </thead>
            <tbody>
              {selectedPlans.map((plan) => (
                <tr key={plan.code}>
                  <td>{plan.name}</td>
                  <td>
                    <div className="d-flex gap-3 align-items-center">
                      <div className="flex-fill">
                        <small className="text-muted">Threshold</small>
                        {renderPercentageDropdown(
                          answers[6]?.value?.[plan.code]?.threshold,
                          (value) =>
                            handleAnswerChange(6, {
                              ...answers[6]?.value,
                              [plan.code]: {
                                ...answers[6]?.value?.[plan.code],
                                threshold: value,
                              },
                            })
                        )}
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted">Target</small>
                        {renderPercentageDropdown(
                          answers[6]?.value?.[plan.code]?.target,
                          (value) =>
                            handleAnswerChange(6, {
                              ...answers[6]?.value,
                              [plan.code]: {
                                ...answers[6]?.value?.[plan.code],
                                target: value,
                              },
                            })
                        )}
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted">Stretch</small>
                        {renderPercentageDropdown(
                          answers[6]?.value?.[plan.code]?.stretch,
                          (value) =>
                            handleAnswerChange(6, {
                              ...answers[6]?.value,
                              [plan.code]: {
                                ...answers[6]?.value?.[plan.code],
                                stretch: value,
                              },
                            })
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );

    case 7:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_VESTING_METHODOLOGY.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_VESTING_METHODOLOGY.description}
          </InfoBox>
          <Table bordered>
        <thead>
          <tr>
            <th>Grade</th>
            <th>Vesting Methodology</th>
          </tr>
        </thead>
        <tbody>
          {data.grades
            .filter((grade) => {
          const eligibility = answers[1]?.value?.[grade.code];
          return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
            })
            .map((grade) => (
          <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
            <td>{grade.code}</td>
            <td>
              <Form.Select
            multiple
            required={true}
            style={{ fontSize: "0.8rem" }}
            value={Array.isArray(answers[7]?.value?.[grade.code]) ? answers[7]?.value?.[grade.code] : []}
            onChange={(e) => {
              const selected = Array.from(
                e.target.selectedOptions,
                (option) => option.value
              );
              handleAnswerChange(7, {
                ...answers[7]?.value,
                [grade.code]: selected,
              });
            }}
              >
            {Object.entries(QUESTIONS.LTI_VESTING_METHODOLOGY.options).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
              </Form.Select>
              {Array.isArray(answers[7]?.value?.[grade.code]) && 
               answers[7]?.value?.[grade.code].includes("other") && (
                <Form.Control
                  type="text"
                  placeholder="Please specify other vesting methodology"
                  value={answers[7]?.value?.[grade.code + "_other"] || ""}
                  onChange={(e) =>
                    handleAnswerChange(7, {
                      ...answers[7]?.value,
                      [grade.code + "_other"]: e.target.value,
                    })
                  }
                  style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}
                  required={true}
                />
              )}
            </td>
          </tr>
            ))}
        </tbody>
          </Table>
        </div>
      );

    case 8: {
      // Determine eligible grades
      const eligibleGrades = data.grades.filter((grade) => {
        const eligibility = answers[1]?.value?.[grade.code];
        return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
      });
      // Gather all unique selected plans for eligible grades from case 4
      const selectedPlansSet = new Set();
      eligibleGrades.forEach((grade) => {
        const plans = answers[4]?.value?.[grade.code];
        if (Array.isArray(plans)) {
          plans.forEach((plan) => selectedPlansSet.add(plan));
        }
      });
      // Map of plan codes to display names
      const planDisplayNames = QUESTIONS?.LTI_PLANS?.options || {};
      const selectedPlans = Array.from(selectedPlansSet);
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_VESTING_PERIOD.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_VESTING_PERIOD.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                {selectedPlans.map((plan) => (
                  <th key={plan}>{planDisplayNames[plan] || plan}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {eligibleGrades.map((grade) => (
                <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                  <td>{grade.name}</td>
                  {selectedPlans.map((plan) => (
                    <td key={plan}>
                      <Form.Select
                        style={{ fontSize: "0.8rem" }}
                        value={answers[8]?.value?.[grade.code]?.[plan] || []}
                        multiple={true}
                        onChange={(e) => {
                          const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
                          handleAnswerChange(8, {
                            ...answers[8]?.value,
                            [grade.code]: {
                              ...answers[8]?.value?.[grade.code],
                              [plan]: selectedOptions,
                            },
                          });
                        }}
                      >
                        <option value="">Select period</option>
                        {Object.entries(QUESTIONS.LTI_VESTING_PERIOD.options).map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }

    case 9: {
      // Determine eligible grades
      const eligibleGrades = data.grades.filter((grade) => {
        const eligibility = answers[1]?.value?.[grade.code];
        return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
      });
      // Gather all unique selected plans for eligible grades from case 4
      const selectedPlansSet = new Set();
      eligibleGrades.forEach((grade) => {
        const plans = answers[4]?.value?.[grade.code];
        if (Array.isArray(plans)) {
          plans.forEach((plan) => selectedPlansSet.add(plan));
        }
      });
      // Map of plan codes to display names
      const planDisplayNames = QUESTIONS?.LTI_PLANS?.options || {};
      const selectedPlans = Array.from(selectedPlansSet);
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_SETTLEMENT_METHOD.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_SETTLEMENT_METHOD.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                {selectedPlans.map((plan) => (
                  <th key={plan}>{planDisplayNames[plan] || plan}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {eligibleGrades.map((grade) => (
                <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                  <td>{grade.code}</td>
                  {selectedPlans.map((plan) => (
                    <td key={plan}>
                      <Form.Select
                        // required={true} // Removed to allow empty selection, later only disable for grades not eligible
                        style={{ fontSize: "0.8rem" }}
                        value={answers[9]?.value?.[grade.code]?.[plan] || ""}
                        onChange={(e) =>
                          handleAnswerChange(9, {
                            ...answers[9]?.value,
                            [grade.code]: {
                              ...answers[9]?.value?.[grade.code],
                              [plan]: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="">Select method</option>
                      {Object.entries(QUESTIONS.LTI_SETTLEMENT_METHOD.options).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                      </Form.Select>
                      {answers[9]?.value?.[grade.code]?.[plan] === "other" && (
                        <Form.Control
                          type="text"
                          placeholder="Please specify other method"
                          value={answers[9]?.value?.[grade.code]?.[`${plan}_other_detail`] || ""}
                          onChange={(e) =>
                            handleAnswerChange(9, {
                              ...answers[9]?.value,
                              [grade.code]: {
                                ...answers[9]?.value?.[grade.code],
                                [`${plan}_other_detail`]: e.target.value,
                              },
                            })
                          }
                          style={{ 
                            fontSize: "0.8rem", 
                            marginTop: "0.25rem",
                            border: `1px solid ${THEME.grey300}`,
                            borderRadius: "4px"
                          }}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }

    case 10:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_ELIGIBILITY.title}</h5>
          <InfoBox>
            {QUESTIONS.STI_ELIGIBILITY.description}
          </InfoBox>
          <GradeTable
            grades={data.grades}
            renderCell={(grade) =>
              renderSTIEligibility(
                grade,
                answers[10]?.value?.[grade.code],
                (gradeCode, value) =>
                  handleAnswerChange(10, {
                    ...answers[10]?.value,
                    [gradeCode]: value,
                  })
              )
            }
          />
        </div>
      );

    case 11:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_ALLOCATION.title}</h5>
          <InfoBox>
            {QUESTIONS.STI_ALLOCATION.description}
          </InfoBox>
          <Form.Select
            required={true}
            value={answers[11]?.value?.value || ""}
            onChange={(e) =>
              handleAnswerChange(11, {
                value: e.target.value,
              })
            }
            className="w-50"
          >
            <option value="">Select allocation type</option>
            {Object.entries(QUESTIONS.STI_ALLOCATION.options).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Form.Select>
          {answers[11]?.value?.value === "other" && (
            <Form.Control
              type="text"
              placeholder="Please specify other allocation type"
              value={answers[11]?.value?.otherValue || ""}
              onChange={(e) =>
                handleAnswerChange(11, {
                  value: "other",
                  otherValue: e.target.value,
                })
              }
              className="w-50 mt-2"
              required={true}
            />
          )}
        </div>
      );

    case 12:
        return (
          <div className="question-content">
            <h5>{QUESTIONS.STI_REWARD_PERCENTAGES.title}</h5>
            <InfoBox>
              {QUESTIONS.STI_REWARD_PERCENTAGES.description}
            </InfoBox>
            <Table bordered>
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Base Salary</th>
                  <th>Total Guaranteed Package</th>
                  <th>Other Remuneration Base</th>
                </tr>
              </thead>
              <tbody>
                {data.grades
                  .filter(grade => {
                    const eligibility = answers[10]?.value?.[grade.code];
                    return eligibility === 'All' || eligibility === 'Some';
                  })
                  .map(grade => (
                    <tr key={grade.code} style={{ fontSize: '0.8rem' }}>
                      <td>{grade.code}</td>
                      {[
                        'base_salary',
                        'tgp',
                        'other'
                      ].map(type => (
                        <td key={type}>
                          {renderPercentageDropdown(
                            answers[12]?.value?.[grade.code]?.[type],
                            (value) => handleAnswerChange(12, {
                              ...answers[12]?.value,
                              [grade.code]: {
                                ...answers[12]?.value?.[grade.code],
                                [type]: value
                              }
                            })
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        );

    case 13:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_PLAN_TYPE.title}</h5>
          <InfoBox>
            {QUESTIONS.STI_PLAN_TYPE.description}
          </InfoBox>
          <Form.Select
            required={true}
            value={answers[13]?.value?.value || ""}
            onChange={(e) => {
              const newValue = e.target.value;
              // Reset details when changing main selection
              const newDetails =
                newValue === "bonus_pool_performance"
                  ? { measure: "", percentage: "" }
                  : newValue === "bonus_pool_earnings" ||
                    newValue === "hybrid" ||
                    newValue === "other"
                  ? { additionalDetails: "" }
                  : newValue === "single_incentive"
                  ? { percentage: "" }
                  : {};

              handleAnswerChange(13, {
                ...answers[13]?.value,
                value: newValue,
                details: newDetails,
              });
            }}
            className="w-50 mb-3"
          >
            <option value="">Select plan type</option>
            {Object.entries(QUESTIONS.STI_PLAN_TYPE.options).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </Form.Select>

          {answers[13]?.value?.value === "single_incentive" && (
            <div className="mt-4">
              <h6 style={{ color: THEME.grey800 }}>Single Incentive Details</h6>
              <Form.Group className="w-50">
                <Form.Label>
                  What percentage is typically allocated to Short-Term Incentives (Your response here and to your response regarding LTI types should equal 100%):
                </Form.Label>
                <Form.Control
                  required={true}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={answers[13]?.value?.details?.percentage || ""}
                  onChange={(e) =>
                    handleAnswerChange(13, {
                      ...answers[13]?.value,
                      details: {
                        ...answers[13]?.value?.details,
                        percentage: e.target.value,
                      },
                    })
                  }
                  placeholder="Enter percentage"
                />
              </Form.Group>
            </div>
          )}

          {answers[13]?.value?.value === "bonus_pool_performance" && (
            <div className="mt-4">
              <h6 style={{ color: THEME.grey800 }}>Pool Calculation Details</h6>
              <div className="d-flex gap-4 align-items-start">
                <Form.Group className="w-50">
                  <Form.Label>
                    Select measure used to calculate the pool:
                  </Form.Label>
                  <Form.Select
                    multiple={true}
                    required={true}
                    value={answers[13]?.value?.details?.measure || []}
                    onChange={(e) => {
                      const selectedOptions = Array.from(
                        e.target.selectedOptions,
                        (option) => option.value
                      );
                      handleAnswerChange(13, {
                        ...answers[13]?.value, // Preserve existing answer structure
                        details: {
                          ...answers[13]?.value?.details,
                          measure: selectedOptions,
                          // Reset other_measure when changing measure
                          other_measure: selectedOptions.includes("other")
                            ? ""
                            : undefined,
                        },
                      });
                    }}
                  >
                    <option value="">Select measure</option>
                    <option value="ebitda">EBITDA</option>
                    <option value="pat">PAT</option>
                    <option value="revenue">Revenue</option>
                    <option value="pbt">PBT</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>

                {answers[13]?.value?.details?.measure === "other" && (
                  <Form.Group className="w-50">
                    <Form.Label>Specify other measure:</Form.Label>
                    <Form.Control
                      required={true}
                      type="text"
                      value={answers[13]?.value?.details?.other_measure || ""}
                      onChange={(e) =>
                        handleAnswerChange(13, {
                          ...answers[13]?.value, 
                          details: {
                            ...answers[13]?.value?.details,
                            other_measure: e.target.value,
                          },
                        })
                      }
                      placeholder="Enter measure"
                    />
                  </Form.Group>
                )}

                <Form.Group className="w-50">
                  <Form.Label>
                    Percentage used to determine the pool:
                  </Form.Label>
                  <Form.Control
                    required={true}
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={answers[13]?.value?.details?.percentage || ""}
                    onChange={(e) =>
                      handleAnswerChange(13, {
                        ...answers[13]?.value, 
                        details: {
                          ...answers[13]?.value?.details,
                          percentage: e.target.value,
                        },
                      })
                    }
                    placeholder="Enter percentage"
                  />
                </Form.Group>
              </div>
            </div>
          )}

          {(answers[13]?.value?.value === "bonus_pool_earnings" ||
            answers[13]?.value?.value === "hybrid" ||
            answers[13]?.value?.value === "other") && (
            <Form.Group className="mt-4">
              <Form.Label>Please provide additional details:</Form.Label>
              <Form.Control
                as="textarea"
                required={true}
                rows={3}
                value={answers[13]?.value?.details?.additionalDetails || ""}
                onChange={(e) =>
                  handleAnswerChange(13, {
                    ...answers[13]?.value, // Preserve existing answer structure
                    details: {
                      ...answers[13]?.value?.details,
                      additionalDetails: e.target.value,
                    },
                  })
                }
                placeholder="Enter additional details about the plan type"
              />
            </Form.Group>
          )}
        </div>
      );

    case 14:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_PERFORMANCE_ASSESSMENT.title}</h5>
          <InfoBox>
            {QUESTIONS.STI_PERFORMANCE_ASSESSMENT.description}
          </InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                <th>Company performance</th>
                <th>Team performance</th>
                <th>Individual performance</th>
                <th>Other</th>
              </tr>
            </thead>
            <tbody>
              {data.grades
                .filter((grade) => {
                  const eligibility = answers[10]?.value?.[grade.code];
                  return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                })
                .map((grade) => (
                  <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                    <td>{grade.name}</td>
                    {["company", "team", "individual", "other"].map((type) => {
                      // Calculate the sum of the other types for this grade
                      const otherTypes = [
                        "company",
                        "team",
                        "individual",
                        "other",
                      ].filter((t) => t !== type);
                      const otherSum = otherTypes.reduce((sum, t) => {
                        const val = parseFloat(
                          answers[14]?.value?.[grade.code]?.[t] || 0
                        );
                        return sum + (isNaN(val) ? 0 : val);
                      }, 0);

                      // The current value for this type
                      const currentValue =
                        answers[14]?.value?.[grade.code]?.[type] || "";

                      // Calculate max value so that the sum does not exceed 100
                      const maxValue = 100 - otherSum;

                      return (
                        <td key={type}>
                          <Form.Control
                            type="number"
                            value={currentValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Validate that the new value doesn't exceed maxValue
                              const numValue = parseFloat(value);
                              if (value === "" || (numValue >= 0 && numValue <= maxValue)) {
                                handleAnswerChange(14, {
                                  ...answers[14]?.value,
                                  [grade.code]: {
                                    ...answers[14]?.value?.[grade.code],
                                    [type]: value,
                                  },
                                });
                              }
                            }}
                            required={true}
                            style={{ fontSize: "0.8rem" }}
                            min="0"
                            max={maxValue}
                            step="0.01"
                            placeholder="0.00"
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      );

    case 15:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_PERFORMANCE_CONDITIONS.title}</h5>
          <InfoBox>
            {QUESTIONS.STI_PERFORMANCE_CONDITIONS.description}
          </InfoBox>
          <Form.Group>
            <Form.Select
              multiple={true}
              required={true}
              value={answers[15]?.value?.value || []}
              onChange={(e) => {
                const selected = Array.from(
                  e.target.selectedOptions,
                  (option) => option.value
                );
                handleAnswerChange(15, {
                  value: selected,
                });
              }}
              className="mb-3"
              style={{
                border: `1px solid ${THEME.grey300}`,
                "&:focus": {
                  borderColor: THEME.primary,
                  boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
                },
              }}
            >
              <option value="">Select measurement</option>
              {Object.entries(QUESTIONS.STI_PERFORMANCE_CONDITIONS.options).map(([key, value]) => (
                <option key={key} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
            {(answers[15]?.value?.value || []).includes("Other") && (
              <Form.Control
                type="text"
                placeholder="Please specify other performance conditions"
                value={answers[15]?.value?.other || ""}
                onChange={(e) => {
                  handleAnswerChange(15, {
                    ...answers[15]?.value,
                    other: e.target.value,
                  });
                }}
                className="mt-2"
                style={{
                  border: `1px solid ${THEME.grey300}`,
                  "&:focus": {
                    borderColor: THEME.primary,
                    boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
                  },
                }}
              />
            )}
          </Form.Group>
        </div>
      );

    case 16:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_PERFORMANCE_MODIFIERS.title}</h5>
          <InfoBox>
            {QUESTIONS.STI_PERFORMANCE_MODIFIERS.description}
          </InfoBox>
          <Form.Group>
            <Form.Select
              multiple={true}
              required={true}
              value={answers[16]?.value?.value || []} // Initialize as empty array for multiple selections
              onChange={(e) => {
                const selected = Array.from(
                  e.target.selectedOptions,
                  (option) => option.value
                );
                handleAnswerChange(16, {
                  value: selected,
                });
              }}
              className="mb-3"
              style={{
                border: `1px solid ${THEME.grey300}`,
                "&:focus": {
                  borderColor: THEME.primary,
                  boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
                },
              }}
            >
              <option value="">Select performance level</option>
              {Object.entries(QUESTIONS.STI_PERFORMANCE_MODIFIERS.options).map(([key, value]) => (
                <option key={key} value={value}>
                  {value}
                </option>
              ))}
            </Form.Select>
            {(answers[16]?.value?.value || []).includes("Other") && (
              <Form.Control
                type="text"
                placeholder="Please specify other performance modifiers"
                value={answers[16]?.value?.other || ""}
                onChange={(e) => {
                  handleAnswerChange(16, {
                    ...answers[16]?.value,
                    other: e.target.value,
                  });
                }}
                className="mt-2"
                style={{
                  border: `1px solid ${THEME.grey300}`,
                  "&:focus": {
                    borderColor: THEME.primary,
                    boxShadow: `0 0 0 0.2rem ${THEME.primaryLight}`,
                  },
                  
                }}
              />
            )}
          </Form.Group>
        </div>
      );

    case 17:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.LTI_TARGET_ANNUAL_AWARDS.title}</h5>
          <InfoBox>
            {QUESTIONS.LTI_TARGET_ANNUAL_AWARDS.description}
          </InfoBox>
          <Form.Group className="mb-3">
            <Form.Select
              value={answers[17]?.value?.base || ""}
              onChange={(e) =>
                handleAnswerChange(17, {
                  ...answers[17]?.value,
                  base: e.target.value,
                })
              }
              required={true}
              style={{ fontSize: "0.8rem" }}
            >
              <option value="">Select base...</option>
              {Object.entries(QUESTIONS.LTI_TARGET_ANNUAL_AWARDS.options).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          
          {(answers[17]?.value?.base === "percentage_base" || 
            answers[17]?.value?.base === "percentage_tgp") && (
            <>
              <InfoBox>
              Indicate the threshold, target and stretch annual long-term incentive awards by grade, expressed as percentages of the applicable remuneration type provided. If awards are not based on percentages of any remuneration type, please specify the detail.
              </InfoBox>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Threshold</th>
                    <th>Target</th>
                    <th>Stretch</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grades
                    .filter((grade) => {
                      const eligibility = answers[1]?.value?.[grade.code];
                      return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                    })
                    .map((grade) => (
                      <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                        <td>{grade.code}</td>
                        {Object.entries(QUESTIONS.LTI_TARGET_ANNUAL_AWARDS.options_awards).map(([value, label]) => (
                          <td key={value}>
                            <Form.Control
                              required={true}
                              type="number"
                              min={value === 'threshold' ? 0 : 
                                   value === 'target' ? (answers[17]?.value?.percentages?.[grade.code]?.threshold || 0) :
                                   (answers[17]?.value?.percentages?.[grade.code]?.target || answers[17]?.value?.percentages?.[grade.code]?.threshold || 0)}
                              max="400"
                              step="0.01"
                              value={answers[17]?.value?.percentages?.[grade.code]?.[value] || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                handleAnswerChange(17, {
                                  ...answers[17]?.value,
                                  percentages: {
                                    ...answers[17]?.value?.percentages,
                                    [grade.code]: {
                                      ...answers[17]?.value?.percentages?.[grade.code],
                                      [value]: newValue,
                                    },
                                  },
                                });
                              }}
                              onInput={(e) => {
                                // Clear custom validation when user starts typing
                                e.target.setCustomValidity('');
                              }}
                              placeholder={`Enter ${label} %`}
                              style={{ fontSize: "0.8rem" }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </>
          )}

          {answers[17]?.value?.base === "monetary_value" && (
            <>
              <InfoBox>
              Indicate the threshold, target and stretch annual long-term incentive awards by grade, expressed as monthly monetary values.
              </InfoBox>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Threshold</th>
                    <th>Target</th>
                    <th>Stretch</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grades
                    .filter((grade) => {
                      const eligibility = answers[1]?.value?.[grade.code];
                      return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                    })
                    .map((grade) => (
                      <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                        <td>{grade.code}</td>
                        {Object.entries(QUESTIONS.LTI_TARGET_ANNUAL_AWARDS.options_awards).map(([value, label]) => (
                          <td key={value}>
                            <Form.Control
                              required={true}
                              type="number"
                              min={value === 'threshold' ? 0 : 
                                   value === 'target' ? (answers[17]?.value?.monetary_value?.[grade.code]?.threshold || 0) :
                                   (answers[17]?.value?.monetary_value?.[grade.code]?.target || answers[17]?.value?.monetary_value?.[grade.code]?.threshold || 0)}
                              step="0.01"
                              value={answers[17]?.value?.monetary_value?.[grade.code]?.[value] || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                handleAnswerChange(17, {
                                  ...answers[17]?.value,
                                  monetary_value: {
                                    ...answers[17]?.value?.monetary_value,
                                    [grade.code]: {
                                      ...answers[17]?.value?.monetary_value?.[grade.code],
                                      [value]: newValue,
                                    },
                                  },
                                });
                              }}
                              onInput={(e) => {
                                // Clear custom validation when user starts typing
                                e.target.setCustomValidity('');
                              }}
                              placeholder={`Enter ${label} amount`}
                              style={{ fontSize: "0.8rem" }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </>
          )}

          {answers[17]?.value?.base === "other" && (
            <>
              <InfoBox>
              Please specify the other remuneration base for each grade.
              </InfoBox>
              <Table bordered>
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th>Other Remuneration Base</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grades
                    .filter((grade) => {
                      const eligibility = answers[1]?.value?.[grade.code];
                      return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                    })
                    .map((grade) => (
                      <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                        <td>{grade.code}</td>
                        <td>
                          <Form.Control
                            required={true}
                            type="text"
                            value={answers[17]?.value?.other?.[grade.code] || ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              
                              handleAnswerChange(17, {
                                ...answers[17]?.value,
                                other: {
                                  ...answers[17]?.value?.other,
                                  [grade.code]: newValue,
                                },
                              });
                            }}
                            placeholder="Enter other remuneration base"
                            style={{ fontSize: "0.8rem" }}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      );

    case 18:
    return (
      <div className="question-content">
        <h5>{QUESTIONS.STI_TARGET_ANNUAL_AWARDS.title}</h5>
        <InfoBox>
          {QUESTIONS.STI_TARGET_ANNUAL_AWARDS.description}
        </InfoBox>
        <Form.Group>
          <Form.Select
            value={answers[18]?.value?.base || ""}
            onChange={(e) =>
              handleAnswerChange(18, {
                ...answers[18]?.value,
                base: e.target.value,
              })
            }
            required={true}
            style={{ fontSize: "0.8rem" }}
          >
            <option value="">Select remuneration base...</option>
            {Object.entries(QUESTIONS.STI_TARGET_ANNUAL_AWARDS.options).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Form.Select>
        </Form.Group>
        
        {(answers[18]?.value?.base === "percentage_base" || 
          answers[18]?.value?.base === "percentage_tgp") && (
          <>
            <InfoBox>
            Indicate the threshold, target and stretch annual short-term incentive awards by grade, expressed as percentages of the applicable remuneration type provided. If awards are not based on percentages of any remuneration type, please specify the detail.
            </InfoBox>
            <Table bordered>
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Threshold</th>
                  <th>Target</th>
                  <th>Stretch</th>
                </tr>
              </thead>
              <tbody>
                {data.grades
                  .filter((grade) => {
                    const eligibility = answers[10]?.value?.[grade.code];
                    return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                  })
                  .map((grade) => (
                    <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                      <td>{grade.code}</td>
                      {Object.entries(QUESTIONS.STI_TARGET_ANNUAL_AWARDS.options_awards).map(([value, label]) => (
                          <td key={value}>
                            <Form.Control
                              required={true}
                              type="number"
                              min={value === 'threshold' ? 0 : 
                                   value === 'target' ? (answers[18]?.value?.percentages?.[grade.code]?.threshold || 0) :
                                   (answers[18]?.value?.percentages?.[grade.code]?.target || answers[18]?.value?.percentages?.[grade.code]?.threshold || 0)}
                              max="400"
                              step="0.01"
                              value={answers[18]?.value?.percentages?.[grade.code]?.[value] || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                handleAnswerChange(18, {
                                  ...answers[18]?.value,
                                  percentages: {
                                    ...answers[18]?.value?.percentages,
                                    [grade.code]: {
                                      ...answers[18]?.value?.percentages?.[grade.code],
                                      [value]: newValue,
                                    },
                                  },
                                });
                              }}
                              onInput={(e) => {
                                // Clear custom validation when user starts typing
                                e.target.setCustomValidity('');
                              }}
                              placeholder={`Enter ${label} %`}
                              style={{ fontSize: "0.8rem" }}
                            />
                          </td>
                        ))}
                    </tr>
                  ))}
              </tbody>
            </Table>
          </>
        )}

        {answers[18]?.value?.base === "monetary_value" && (
          <>
            <InfoBox>
            Indicate the threshold, target and stretch annual short-term incentive awards by grade, expressed as monetary values.
            </InfoBox>
            <Table bordered>
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Threshold</th>
                  <th>Target</th>
                  <th>Stretch</th>
                </tr>
              </thead>
              <tbody>
                {data.grades
                  .filter((grade) => {
                    const eligibility = answers[10]?.value?.[grade.code];
                    return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                  })
                  .map((grade) => (
                    <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                      <td>{grade.code}</td>
                      {Object.entries(QUESTIONS.STI_TARGET_ANNUAL_AWARDS.options_awards).map(([value, label]) => (
                          <td key={value}>
                            <Form.Control
                              required={true}
                              type="number"
                              min="0"
                              step="0.01"
                              value={answers[18]?.value?.monetary?.[grade.code]?.[value] || ""}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                
                                handleAnswerChange(18, {
                                  ...answers[18]?.value,
                                  monetary: {
                                    ...answers[18]?.value?.monetary,
                                    [grade.code]: {
                                      ...answers[18]?.value?.monetary?.[grade.code],
                                      [value]: newValue,
                                    },
                                  },
                                });
                              }}
                              placeholder={`Enter ${label} amount`}
                              style={{ fontSize: "0.8rem" }}
                            />
                          </td>
                        ))}
                    </tr>
                  ))}
              </tbody>
            </Table>
          </>
        )}

        {answers[18]?.value?.base === "other" && (
          <>
            <InfoBox>
            Please specify the other remuneration base for each grade.
            </InfoBox>
            <Table bordered>
              <thead>
                <tr>
                  <th>Grade</th>
                  <th>Other Remuneration Base</th>
                </tr>
              </thead>
              <tbody>
                {data.grades
                  .filter((grade) => {
                    const eligibility = answers[10]?.value?.[grade.code];
                    return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                  })
                  .map((grade) => (
                    <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                      <td>{grade.code}</td>
                      <td>
                        <Form.Control
                          required={true}
                          type="text"
                          value={answers[18]?.value?.other?.[grade.code] || ""}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            
                            handleAnswerChange(18, {
                              ...answers[18]?.value,
                              other: {
                                ...answers[18]?.value?.other,
                                [grade.code]: newValue,
                              },
                            });
                          }}
                          placeholder="Enter other remuneration base"
                          style={{ fontSize: "0.8rem" }}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </>
        )}
      </div>
    );

    case 19:
      return (
        <div className="question-content">
          <h5>{QUESTIONS.STI_PLAN_TYPE_V2.title}</h5>
          <InfoBox>{QUESTIONS.STI_PLAN_TYPE_V2.description}</InfoBox>
          <Table bordered>
            <thead>
              <tr>
                <th>Grade</th>
                {Object.entries(QUESTIONS.STI_PLAN_TYPE_V2.options).map(([key, label]) => (
                  <th key={key}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.grades
                .filter((grade) => {
                  const eligibility = answers[10]?.value?.[grade.code];
                  return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
                })
                .map((grade) => (
                  <tr key={grade.code} style={{ fontSize: "0.8rem" }}>
                    <td>{grade.code}</td>
                    {Object.keys(QUESTIONS.STI_PLAN_TYPE_V2.options).map((optionKey) => (
                      <td key={optionKey} className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={
                            Array.isArray(answers[19]?.value?.[grade.code]?.selected)
                              ? answers[19]?.value?.[grade.code]?.selected.includes(optionKey)
                              : false
                          }
                          onChange={(e) => {
                            const prevValue = answers[19]?.value || {};
                            const prevGrade = prevValue[grade.code] || {};
                            const prevSelected = Array.isArray(prevGrade.selected) ? prevGrade.selected : [];
                            const prevDetails = prevGrade.details || {};

                            let newSelected;
                            let newDetails = { ...prevDetails };

                            if (e.target.checked) {
                              newSelected = [...prevSelected, optionKey];
                              // No need to add details here, will be added when user fills in
                            } else {
                              newSelected = prevSelected.filter((k) => k !== optionKey);
                              // Remove the details for this plan type
                              if (newDetails.hasOwnProperty(optionKey)) {
                                delete newDetails[optionKey];
                              }
                            }

                            // If both selected and details are empty, remove the grade entry entirely
                            let newValue = { ...prevValue };
                            if (newSelected.length === 0 && Object.keys(newDetails).length === 0) {
                              // Remove the grade entry
                              newValue = { ...newValue };
                              delete newValue[grade.code];
                            } else {
                              newValue = {
                                ...newValue,
                                [grade.code]: {
                                  ...prevGrade,
                                  selected: newSelected,
                                  details: newDetails,
                                },
                              };
                            }

                            handleAnswerChange(19, newValue);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
          {/* For each grade, for each plan type selected, expand the form for details as in case 13 */}
          {data.grades
            .filter((grade) => {
              const eligibility = answers[10]?.value?.[grade.code];
              return eligibility?.toLowerCase() === "all" || eligibility?.toLowerCase() === "some";
            })
            .map((grade) => {
              const selectedPlanTypes = Array.isArray(answers[19]?.value?.[grade.code]?.selected)
                ? answers[19]?.value?.[grade.code]?.selected
                : [];
              if (selectedPlanTypes.length === 0) return null;
              return (
                <div
                  key={`details-group-${grade.code}`}
                  className="mb-4 p-3"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    background: "#fafbfc",
                    marginTop: "1.5rem",
                  }}
                >
                  <h6 className="mb-3" style={{ fontWeight: 600 }}>
                    STI Plan Details for Grade <span style={{ color: REMCHANNEL_COLORS.green }}>{grade.code}</span>
                  </h6>
                  {selectedPlanTypes.map((optionKey) => {
                    const planLabel = QUESTIONS.STI_PLAN_TYPE_V2.options[optionKey];
                    // Ensure details object exists for this grade and plan type, now nested under value
                    const details =
                      (answers[19]?.value &&
                        answers[19]?.value[grade.code] &&
                        answers[19]?.value[grade.code].details &&
                        answers[19]?.value[grade.code].details[optionKey]) ||
                      {};

                    // Helper to update details for a specific grade and plan type, nested under value
                    const updateDetails = (newDetails) => {
                      const prevValue = answers[19]?.value || {};
                      const prevGrade = prevValue[grade.code] || {};
                      const prevDetails = prevGrade.details || {};
                      const currentSelected = prevGrade.selected || [];

                      // Clean up: if newDetails is empty, remove the plan type from details
                      let newDetailsStructure = { ...prevDetails };
                      if (
                        newDetails &&
                        Object.keys(newDetails).length === 0 &&
                        newDetailsStructure.hasOwnProperty(optionKey)
                      ) {
                        delete newDetailsStructure[optionKey];
                      } else {
                        newDetailsStructure = {
                          ...newDetailsStructure,
                          [optionKey]: {
                            ...details,
                            ...newDetails,
                          },
                        };
                      }

                      // If both selected and details are empty, remove the grade entry entirely
                      let newValue = { ...prevValue };
                      if (
                        (currentSelected.length === 0 || !currentSelected) &&
                        Object.keys(newDetailsStructure).length === 0
                      ) {
                        newValue = { ...newValue };
                        delete newValue[grade.code];
                      } else {
                        newValue = {
                          ...newValue,
                          [grade.code]: {
                            ...prevGrade,
                            selected: currentSelected,
                            details: newDetailsStructure,
                          },
                        };
                      }

                      handleAnswerChange(19, newValue);
                    };

                    return (
                      <div
                        key={`${grade.code}-${optionKey}`}
                        className="mb-4 p-3"
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          background: "#fff",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <div className="mb-2">
                          <span style={{ fontWeight: 500 }}>
                            <h5>{planLabel}</h5>
                          </span>
                        </div>
                        {/* Mark as "Applied" for the following plan types */}
                        {["weighted_formula", "multiplicative_formula", "bonus_pool_earnings", "hybrid"].includes(optionKey) && (
                          <div className="mb-2">
                            <span
                              style={{
                                display: "inline-block",
                                background: "#e6f4ea",
                                color: "#218838",
                                padding: "0.25rem 0.75rem",
                                borderRadius: "16px",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                              }}
                            >
                              Applied
                            </span>
                          </div>
                        )}

                        {/* "Other" - show multiline text box */}
                        {optionKey === "other" && (
                          <div className="mb-2">
                            <Form.Label>
                              Please specify the other STI plan type for grade <b>{grade.code}</b>:
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              required={true}
                              value={details.otherText || ""}
                              onChange={(e) => {
                                updateDetails({ otherText: e.target.value });
                              }}
                              placeholder="Enter other STI plan type details"
                              style={{ fontSize: "0.8rem", maxWidth: 500 }}
                            />
                          </div>
                        )}

                        {/* "Single incentive" - show percentage input */}
                        {optionKey === "single_incentive" && (
                          <div className="mb-2">
                            <Form.Label>
                              What percentage is typically allocated to Short-Term Incentives for grade <b>{grade.code}</b> (Your response here will inform the percentage allocated to LTI):
                            </Form.Label>
                            <Form.Control
                              required={true}
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={details.percentage || ""}
                              onChange={(e) => {
                                updateDetails({ percentage: e.target.value });
                              }}
                              placeholder="Enter STI %"
                              style={{ fontSize: "0.8rem", maxWidth: 400 }}
                            />
                          </div>
                        )}

                        {/* "Bonus pool (determined in excess of a specific performance hurdle)" - show measure selection and percentage input */}
                        {optionKey === "bonus_pool_performance" && (
                          <div className="mb-2">
                            <Form.Label>
                              Select measure(s) used to calculate the pool for grade <b>{grade.code}</b> (Hold CTRL to select multiple):
                            </Form.Label>
                            <Form.Select
                              multiple={true}
                              required={true}
                              value={details.measure || []}
                              onChange={(e) => {
                                const selectedOptions = Array.from(
                                  e.target.selectedOptions,
                                  (option) => option.value
                                );
                                updateDetails({ measure: selectedOptions });
                              }}
                              style={{ fontSize: "0.8rem", maxWidth: 400 }}
                            >
                              {Object.entries(QUESTIONS.STI_PERFORMANCE_CONDITIONS.options).map(([key, label]) => (
                                <option key={key} value={key}>
                                  {label}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Label className="mt-2">
                              What percentage of the pool is allocated for this plan type?
                            </Form.Label>
                            <Form.Control
                              required={true}
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={details.percentage || ""}
                              onChange={(e) => {
                                updateDetails({ percentage: e.target.value });
                              }}
                              placeholder="Enter pool percentage (%)"
                              style={{ fontSize: "0.8rem", maxWidth: 400 }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      );

    default:
      return null;
  }
};

const ConfirmationModal = ({ show, title, message, onConfirm, onCancel }) => (
  <BootstrapModal show={show} onHide={onCancel} centered>
    <BootstrapModal.Header closeButton>
      <BootstrapModal.Title>{title}</BootstrapModal.Title>
    </BootstrapModal.Header>
    <BootstrapModal.Body>{message}</BootstrapModal.Body>
    <BootstrapModal.Footer>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onConfirm}>
        Confirm
      </Button>
    </BootstrapModal.Footer>
  </BootstrapModal>
);

const FloatingSaveReminder = ({ show, onSave }) => (
  <div
    className={`floating-save-reminder ${show ? "show" : ""}`}
    style={{
      position: "fixed",
      left: "50%",
      bottom: "2rem",
      transform: show
        ? "translate(-50%, 0)"
        : "translate(-50%, 200%)",
      transition: "transform 0.3s ease-in-out",
      padding: "1rem",
      background: THEME.grey100,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      borderRadius: "8px",
      zIndex: 1000,
      border: `1px solid ${THEME.primaryBorder}`,
      minWidth: 320,
      maxWidth: "90vw",
    }}
  >
    <div className="d-flex align-items-center gap-3 justify-content-center">
      <div style={{ color: THEME.grey800 }}>
        <i className="bi bi-exclamation-circle text-warning me-2"></i>
        You have unsaved local changes
      </div>
      <Button
        size="sm"
        variant="primary"
        onClick={onSave}
        style={{
          backgroundColor: THEME.primary,
          borderColor: THEME.primaryBorder,
          "&:hover": {
            backgroundColor: THEME.primaryDark,
          },
        }}
      >
        Save Now
      </Button>
    </div>
  </div>
);

const LOCAL_STORAGE_KEY = "questionnaire_drafts";

// Update the saveToLocalStorage function to accept orgId parameter
const saveToLocalStorage = (questionnaireId, data, orgId) => {
  try {
    // Get existing drafts from localStorage
    const drafts = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "{}");
    let answers = data.answers || {};
    // If answers is an array, convert to object with questionId as key
    if (Array.isArray(answers)) {
      const objAnswers = {};
      answers.forEach((ans) => {
        if (ans && ans.questionId) objAnswers[String(ans.questionId)] = ans;
      });
      answers = objAnswers;
    }
    // Add organization ID and timestamp to ensure filtering works correctly
    const dataWithOrgId = {
      ...data,
      organizationId: parseInt(orgId),
      lastSaved: new Date().toISOString(),
      answers, // Always use object
      changeHistory: data.changeHistory || {},
    };
    drafts[questionnaireId] = dataWithOrgId;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(drafts));

    const totalQuestions = Object.keys(QUESTIONS).length; 

    // Better detection of actually answered questions - check for meaningful values
    const answeredQuestions = Object.entries(data.answers || {}).filter(
      ([questionId, q]) => {
        if (!q || !q.value) return false;

        // Check if question has a meaningful value based on its type
        const questionNum = parseInt(questionId);
        if (isNaN(questionNum)) return false;

        // Different validation for different question types
        switch (questionNum) {
          case 1: // LTI Eligibility
            return q.value && Object.keys(q.value).length > 0;
          case 2: // LTI Target Annual Awards
            return q.value && Object.keys(q.value).length > 0;
          case 3: // LTI Allocation Methodology
            return q.value && Object.keys(q.value).length > 0;
          case 4: // LTI Plan Types
            return q.value && Object.keys(q.value).length > 0;
          case 5: // LTI Performance Conditions
            return q.value && Object.keys(q.value).length > 0;
          case 6: // Vesting Percentages
            return q.value && Object.keys(q.value).length > 0;
          case 7: // LTI Vesting Methodology
            return q.value && Object.keys(q.value).length > 0;
          case 8: // LTI Vesting Period
            return q.value && Object.keys(q.value).length > 0;
          case 9: // Settlement Method
            return q.value && Object.keys(q.value).length > 0;
          case 10: // STI Eligibility
            return q.value && Object.keys(q.value).length > 0;
          case 11: // STI Allocation Methodology
            return q.value !== undefined && q.value !== null && q.value !== "";
          case 12: // STI Reward Percentages
            return q.value && Object.keys(q.value).length > 0;
          case 13: // STI Plan Type
            return q.value !== undefined && q.value !== null && q.value !== "";
          case 14: // STI Performance Assessment
            return q.value && Object.keys(q.value).length > 0;
          case 15: // STI Performance Measurement
            return q.value !== undefined && q.value !== null && q.value !== "";
          case 16: // STI Performance Modifiers
            return q.value !== undefined && q.value !== null && q.value !== "";
          case 19: // STI Plan Types V2
            return q.value && Object.keys(q.value).length > 0;
          default:
            return false;
        }
      }
    ).length;

    const validatedAnsweredQuestions = Math.min(
      answeredQuestions,
      totalQuestions
    );
    const completionPercentage = Math.round(
      (validatedAnsweredQuestions / totalQuestions) * 100
    );

    return {
      success: true,
      progress: completionPercentage,
      completedQuestions: validatedAnsweredQuestions,
      totalQuestions: totalQuestions,
      lastSaved: dataWithOrgId.lastSaved,
    };
  } catch (error) {
    console.error("Error saving draft to localStorage:", error);
    return { success: false, error: error.message };
  }
};

const loadFromLocalStorage = (questionnaireId) => {
  try {
    const drafts = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
    );
    return drafts[questionnaireId];
  } catch (err) {
    console.error("Failed to load draft with id:", questionnaireId, err);
    return null;
  }
};

const clearLocalStorage = (questionnaireId) => {
  try {
    const drafts = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "{}");
    delete drafts[questionnaireId];
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(drafts));
  } catch (err) {
    console.error("Failed to clear local storage: ", questionnaireId, err);
  }
};

const QuestionNav = ({ currentQuestion, onQuestionClick, answers = {} }) => {
  // Map question numbers to their titles using the QUESTION_NUMBERS object
  const questionList = Object.values(QUESTIONS)
    .filter(q => q.display !== false)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="question-nav-list" style={{ minWidth: 260, width: "100%" }}>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, width: "100%" }}>
        {questionList.map((q, idx) => {
          const active = currentQuestion === idx;
          return (
            <li key={q.displayOrder} style={{ marginBottom: 6 }}>
              <button
                type="button"
                className={`question-nav-list-item${active ? " active" : ""}`}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: active ? "#e8f5e9" : "#fff",
                  border: "1px solid #ced4da",
                  borderLeft: active
                    ? `4px solid ${THEME.primary}`
                    : "1px solid #ced4da",
                  color: "#343a40",
                  fontWeight: active ? 600 : 400,
                  padding: "8px 12px",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  boxShadow: active ? "0 2px 8px rgba(0,166,81,0.08)" : "none",
                  outline: "none",
                }}
                onClick={() => onQuestionClick(idx)}
              >
                <span
                  style={{
                    minWidth: 24,
                    display: "inline-block",
                    textAlign: "center",
                  }}
                >
                  {<span style={{ color: "#343a40" }}>{q.displayOrder}</span>}
                </span>
                <span style={{ flex: 1, color: "#343a40" }}>{q.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Helper to normalize answers array to object
function normalizeAnswersArray(answersArray) {
  if (!Array.isArray(answersArray)) return answersArray;
  const obj = {};
  answersArray.forEach((ans) => {
    if (ans && ans.questionId) {
      obj[String(ans.questionId)] = ans;
    }
  });
  return obj;
}

const NavigationButtons = ({
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  onSave,
  // onSubmit,
  // onSubmitClick,
  loading,
  hasUnsavedChanges,
  showSubmit,
  answers,
}) => {
  return (
    <div className="d-flex justify-content-between align-items-center mt-4">
      <Button
        variant="outline-secondary"
        onClick={onPrevious}
        disabled={currentStep === 0 || loading}
        className="d-flex align-items-center"
        style={{
          color: THEME.grey700,
          borderColor: THEME.grey400,
          backgroundColor: THEME.grey100,
          "&:hover": {
            backgroundColor: THEME.grey200,
          },
        }}
      >
        <i className="bi bi-arrow-left me-2"></i>
        Previous
      </Button>

      <div className="d-flex gap-2">
        <Button
          variant="success"
          onClick={onSave}
          disabled={loading || !hasUnsavedChanges}
          className="d-flex align-items-center"
          style={{
            backgroundColor: THEME.primary,
            borderColor: THEME.primary,
            "&:hover": {
              backgroundColor: THEME.primaryDark,
            },
          }}
        >
          <i className="bi bi-save me-2"></i>
          {loading ? "Saving..." : "Save Progress"}
        </Button>

        {showSubmit && currentStep === totalSteps - 1 && (
          <>
         
            <Button
              variant="primary"
              type="submit"
             
              className="d-flex align-items-center"
              style={{
                backgroundColor: THEME.primaryDark,
                borderColor: THEME.primaryDarker,
                "&:hover": {
                  backgroundColor: THEME.primaryDarker,
                },
              }}
            >
              <i className="bi bi-cloud-upload me-2"></i>
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </>
        )}
        <Button
          variant="outline-secondary"
          onClick={onNext}
          disabled={currentStep === totalSteps - 1 || loading}
          className="d-flex align-items-center"
          style={{
            color: THEME.grey700,
            borderColor: THEME.grey400,
            backgroundColor: THEME.grey100,
            "&:hover": {
              backgroundColor: THEME.grey200,
            },
          }}
        >
          Next
          <i className="bi bi-arrow-right ms-2"></i>
        </Button>
      </div>
    </div>
  );
};

// Add Toast component at the top level, before the Questionnaire component
const ToastMessage = ({ show, variant, message, onClose }) => {
  return (
    <Toast
      show={show}
      onClose={onClose}
      delay={variant === "danger" ? 8000 : 3000}
      autohide
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        minWidth: "300px",
        maxWidth: "500px",
        zIndex: 9999,
        backgroundColor:
          variant === "success" ? THEME.primaryLight : THEME.grey100,
        borderLeft: `4px solid ${
          variant === "success" ? THEME.success : THEME.danger
        }`,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      }}
    >
      <Toast.Header
        style={{
          backgroundColor: "transparent",
          borderBottom: "none",
        }}
      >
        <i
          className={`bi bi-${
            variant === "success" ? "check-circle" : "exclamation-circle"
          } me-2`}
          style={{
            color: variant === "success" ? THEME.success : THEME.danger,
          }}
        />
        <strong className="me-auto">
          {variant === "success" ? "Success" : "Error"}
        </strong>
      </Toast.Header>
      <Toast.Body style={{ color: THEME.grey800 }}>
        <div dangerouslySetInnerHTML={{ __html: message }} />
      </Toast.Body>
    </Toast>
  );
};

const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
  return (
    <Modal show={show} onHide={onCancel} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{message}</p>
        <p className="text-danger">
          <strong>Warning:</strong> This action cannot be undone.
        </p>
        {/* Show questionnaire details */}
        <div className="mt-3 p-3 bg-light rounded">
          <small className="text-muted">
            This will permanently delete the questionnaire and all its data.
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Add the isQuestionCompleted function at the top level
const isNonEmpty = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "string") return val.trim() !== "";
  if (Array.isArray(val)) return val.length > 0 && val.some(isNonEmpty);
  if (typeof val === "object") {
    return Object.values(val).some(isNonEmpty);
  }
  return true;
};

// Helper to unwrap the actual value from the answer object
function getActualValue(answer) {
  if (!answer || answer.value === undefined || answer.value === null)
    return undefined;
  if (
    typeof answer.value === "object" &&
    answer.value.hasOwnProperty("value")
  ) {
    return answer.value.value;
  }
  return answer.value;
}

// Helper to get eligible grades for a question
function getEligibleGrades(questionNumber, answers) {
  if ([2, 3, 4, 5, 6, 7, 8, 9, 17].includes(questionNumber)) {
    // LTI: eligibility from Q1
    const q1Value = getActualValue(answers[1]);
    if (!q1Value || typeof q1Value !== "object") return [];
    return Object.keys(q1Value).filter(
      (grade) => {
        const gradeValue = q1Value[grade];
        return gradeValue && typeof gradeValue === "string" && 
               (gradeValue.toLowerCase() === "all" || gradeValue.toLowerCase() === "some");
      }
    );
  }
  if ([12, 14, 18, 19].includes(questionNumber)) {
    // STI: eligibility from Q10
    const q10Value = getActualValue(answers[10]);
    if (!q10Value || typeof q10Value !== "object") return [];
    return Object.keys(q10Value).filter(
      (grade) => {
        const gradeValue = q10Value[grade];
        return gradeValue && typeof gradeValue === "string" && 
               (gradeValue.toLowerCase() === "all" || gradeValue.toLowerCase() === "some");
      }
    );
  }
  return [];
}

const isQuestionEngaged = (questionNumber, answer, answers) => {
  // console.log(`\n[isQuestionEngaged] questionNumber:`, questionNumber);
  // console.log(`[isQuestionEngaged] answer:`, answer);
  const value = getActualValue(answer);
  // console.log(`[isQuestionEngaged] value:`, value);
  if (value === undefined || value === null) {
    // console.log(`[isQuestionEngaged] => false (value is undefined or null)`);
    return false;
  }
  // Per-grade questions with eligibility (including Q1 and Q10)
  if ([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 19].includes(questionNumber)) {
    const eligibleGrades = getEligibleGrades(questionNumber, answers);
    // console.log(`[isQuestionEngaged] eligibleGrades:`, eligibleGrades);
    if (!value || typeof value !== "object") {
      // console.log(`[isQuestionEngaged] => false (value is not object)`);
      return false;
    }
    // If no eligible grades, consider engaged (skipped)
    if (eligibleGrades.length === 0) {
      // console.log(`[isQuestionEngaged] => true (no eligible grades)`);
      return true;
    }
    // Consider engaged if at least one eligible grade is present as a key in the value object
    const engaged = eligibleGrades.some((grade) =>
      Object.prototype.hasOwnProperty.call(value, grade)
    );
    // console.log(
    //   `[isQuestionEngaged] engaged (at least one eligible grade present):`,
    //   engaged
    // );
    return engaged;
  }
  // Single-value questions
  const engaged = isNonEmpty(value);
  // console.log(`[isQuestionEngaged] single-value engaged:`, engaged);
  return engaged;
};



// Migration: Clean up old drafts in localStorage that use array indices for answers
const migrateDrafts = () => {
  try {
    const drafts = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "{}");
    let changed = false;
    Object.keys(drafts).forEach((draftId) => {
      const draft = drafts[draftId];
      if (draft && draft.answers && Array.isArray(draft.answers)) {
        // Convert array to object with string keys
        const objAnswers = {};
        draft.answers.forEach((val, idx) => {
          if (val && val.questionId) {
            objAnswers[String(val.questionId)] = val;
          }
        });
        draft.answers = objAnswers;
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(drafts));
    }
  } catch (e) {
    // Ignore errors
  }
};

// Call migration on module load
migrateDrafts();

// Add normalization function for answers
function normalizeAnswers(apiAnswers) {
  if (!Array.isArray(apiAnswers)) return apiAnswers;
  const obj = {};
  apiAnswers.forEach((ans) => {
    if (String(ans.questionId) === "0") return; // skip invalid
    const raw = ans.value?.value ?? ans.value;
    // For multi-select questions (e.g. Q15 STI Performance Conditions, Q16 STI Performance Modifiers),
    // the form expects answers[id].value = { value: array, other: string }. Preserve that shape.
    if (raw && Array.isArray(raw)) {
      // Legacy: stored value was just the array
      obj[String(ans.questionId)] = { ...ans, value: { value: raw, other: "" } };
    } else if (raw && typeof raw === "object" && Array.isArray(raw.value)) {
      // New format: stored value was { value: [...], other: "..." }
      obj[String(ans.questionId)] = { ...ans, value: { value: raw.value, other: raw.other ?? "" } };
    } else {
      obj[String(ans.questionId)] = { ...ans, value: ans.value?.value ?? ans.value };
    }
  });
  return obj;
}

function answersMapFromQuestionnaire(q) {
  const a = q?.answers;
  if (a == null) return {};
  if (Array.isArray(a)) {
    const normalized = normalizeAnswers(a);
    return normalized && typeof normalized === "object" ? normalized : {};
  }
  if (typeof a === "object") return { ...a };
  return {};
}

/**
 * Merge browser-local draft answers with server-backed answers. Stale or partial
 * localStorage (e.g. another user's empty draft on a different machine) must not
 * wipe fields saved to the API.
 */
function mergeDraftAnswersWithApi(apiQuestionnaire, draft) {
  const apiAnswers = answersMapFromQuestionnaire(apiQuestionnaire);
  const draftAnswers =
    draft?.answers && typeof draft.answers === "object" && !Array.isArray(draft.answers)
      ? draft.answers
      : {};
  const merged = { ...apiAnswers };
  for (const qid of Object.keys(draftAnswers)) {
    const dVal = draftAnswers[qid];
    const draftContent = dVal?.value !== undefined ? dVal.value : dVal;
    const apiEntry = merged[qid];
    const apiContent = apiEntry?.value !== undefined ? apiEntry.value : apiEntry;
    const draftMeaningful = isNonEmpty(draftContent);
    const apiMeaningful = isNonEmpty(apiContent);
    if (draftMeaningful) merged[qid] = draftAnswers[qid];
    else if (!apiMeaningful) merged[qid] = draftAnswers[qid];
  }
  return merged;
}

const Questionnaire = ({ orgId }) => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [activeGrades, setActiveGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("questionnaires");
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const [answers, setAnswers] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [changeHistory, setChangeHistory] = useState({});
  const [toast, setToast] = useState({
    show: false,
    variant: "success",
    message: "",
  });
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState("");
  const [confirmModalMessage, setConfirmModalMessage] = useState("");
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [formData, setFormData] = useState(null);
  
  // KAM toggle for legacy grades (employee-based vs permission-based)
  const [isKAMUser, setIsKAMUser] = useState(false);
  const [useLegacyGrades, setUseLegacyGrades] = useState(false);
  const isEmbedded = window.self !== window.top;
  
  // Organization settings modal state
  const [showOrgSettings, setShowOrgSettings] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  // Add state for submit confirmation modal
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const formRef = useRef();
  
  // State for view modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewQuestionnaireData, setViewQuestionnaireData] = useState(null);
  const [loadingViewData, setLoadingViewData] = useState(false);

  // Add memoization for orgId
  const memoizedOrgId = React.useMemo(() => orgId || "1066", [orgId]);

  // Helper to clean up answers for questions 5, 6, 8, 9 when plans are unticked in Q4
  const cleanupDeselectedPlans = (newQ4Value) => {
    // Get all selected plans across all grades
    const selectedPlans = new Set();
    Object.values(newQ4Value || {}).forEach((gradeValues) => {
      if (Array.isArray(gradeValues)) {
        gradeValues.forEach((plan) => selectedPlans.add(plan));
      }
    });

    setAnswers((prev) => {
      const updated = { ...prev };

      // Q5: answers[5]?.value?.[planCode]
      if (updated[5]?.value) {
        const valueB = updated[4]?.value?.value_b || {};
        // Remove value_b entries for plans not selected
        if (updated[4]?.value) {
          const newValueB = {};
          Object.keys(valueB).forEach((planCode) => {
            if (selectedPlans.has(planCode)) {
              newValueB[planCode] = valueB[planCode];
            }
          });
          updated[4].value = {
            ...updated[4].value,
            value_b: newValueB,
          };
        }
        // Remove answers[5].value entries for plans not selected
        const newQ5Value = {};
        Object.keys(updated[5].value).forEach((planCode) => {
          if (selectedPlans.has(planCode)) {
            newQ5Value[planCode] = updated[5].value[planCode];
          }
        });
        updated[5] = {
          ...updated[5],
          value: newQ5Value,
        };
      }

      // Q6: answers[6]?.value?.[planCode]
      if (updated[6]?.value) {
        const newQ6Value = {};
        Object.keys(updated[6].value).forEach((planCode) => {
          if (selectedPlans.has(planCode)) {
            newQ6Value[planCode] = updated[6].value[planCode];
          }
        });
        updated[6] = {
          ...updated[6],
          value: newQ6Value,
        };
      }

      // Q8 & Q9: answers[8]?.value?.[grade][plan], answers[9]?.value?.[grade][plan]
      [8, 9].forEach((qNum) => {
        if (updated[qNum]?.value) {
          const newQValue = {};
          Object.keys(updated[qNum].value).forEach((grade) => {
            const gradePlans = updated[qNum].value[grade] || {};
            const newGradePlans = {};
            Object.keys(gradePlans).forEach((planCode) => {
              if (selectedPlans.has(planCode)) {
                newGradePlans[planCode] = gradePlans[planCode];
              }
            });
            newQValue[grade] = newGradePlans;
          });
          updated[qNum] = {
            ...updated[qNum],
            value: newQValue,
          };
        }
      });

      return updated;
    });
  };

  // Function to load questionnaires from API
  const loadQuestionnaires = async () => {
    setLoading(true);
    setError("");
    try {
      // console.log('Loading questionnaires from API for organization:', memoizedOrgId);
      const response = await getQuestionnairesByOrganization();
      // console.log('Questionnaires loaded:', response);

      // Ensure we have an array of questionnaires
      const apiQuestionnaires = Array.isArray(response?.result)
        ? response.result
        : Array.isArray(response)
        ? response
        : [];

      // Get drafts from local storage
      const drafts = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
      );

      // Create a map of API questionnaires for quick lookup
      const apiQuestionnairesMap = new Map(
        apiQuestionnaires.map((q) => [q.questionnaireId, q])
      );

      // Filter drafts to only include those for the current organization
      const currentOrgDrafts = {};
      Object.entries(drafts).forEach(([id, draft]) => {
        if (
          !draft.organizationId ||
          draft.organizationId.toString() === memoizedOrgId
        ) {
          currentOrgDrafts[id] = draft;
        }
      });

      // Process drafts and merge with API questionnaires
      const mergedQuestionnaires = apiQuestionnaires.map((apiQ) => {
        const draft = currentOrgDrafts[apiQ.questionnaireId];
        if (draft) {
          const totalQuestions = Object.keys(QUESTIONS).length;
          const answeredQuestions = draft.questions
            ? Object.values(draft.questions).filter(
                (q) => q && Object.keys(q).length > 0
              ).length
            : 0;
          const completionPercentage = Math.round(
            (answeredQuestions / totalQuestions) * 100
          );

          return {
            ...apiQ,
            ...draft,
            answers: mergeDraftAnswersWithApi(apiQ, draft),
            isDraft: true,
            hasLocalChanges: true,
            progress: completionPercentage,
            completedQuestions: answeredQuestions,
            totalQuestions: totalQuestions,
          };
        }
        return apiQ;
      });

      // Add any new drafts that don't exist in the API
      const newDrafts = Object.entries(currentOrgDrafts)
        .filter(([id]) => !apiQuestionnairesMap.has(id))
        .map(([id, draft]) => {
          const totalQuestions = Object.keys(QUESTIONS).length;
          const answeredQuestions = draft.questions
            ? Object.values(draft.questions).filter(
                (q) => q && Object.keys(q).length > 0
              ).length
            : 0;
          const completionPercentage = Math.round(
            (answeredQuestions / totalQuestions) * 100
          );

          return {
            ...draft,
            questionnaireId: id,
            isDraft: true,
            hasLocalChanges: true,
            organizationId: parseInt(memoizedOrgId),
            progress: completionPercentage,
            completedQuestions: answeredQuestions,
            totalQuestions: totalQuestions,
          };
        });

      setQuestionnaires(
        [...mergedQuestionnaires, ...newDrafts].sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.lastSaved || 0);
          const dateB = new Date(b.updatedAt || b.lastSaved || 0);
          return dateB - dateA; // Sort in descending order (newest first)
        })
      );
      handleSuccess("Questionnaires loaded successfully");
    } catch (err) {
      console.error("Error loading questionnaires:", err);
      handleError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load questionnaires"
      );
      // Set empty array to prevent map errors
      setQuestionnaires([]);
    } finally {
      setLoading(false);
    }
  };


  // Check if user is KAM (for legacy grades toggle)
  useEffect(() => {
    const checkKAMRole = async () => {
      try {
        const roles = await getUserRoles();
        if (isEmbedded) {
          const allowedRoles = [1, 2, 3, 4, 5]; // KAM roles
          const hasRole = roles.some((role) => allowedRoles.includes(role.RoleID));
          setIsKAMUser(hasRole);
        } else {
          // In non-embedded mode, always allow access
          setIsKAMUser(true);
        }
      } catch (error) {
        console.error('Error checking KAM role:', error);
        setIsKAMUser(!isEmbedded);
      }
    };
    checkKAMRole();
  }, [isEmbedded]);

  // Load organization settings when opening settings modal
  const loadOrgSettings = async () => {
    try {
      const settings = await getOrganizationSettings(memoizedOrgId);
      setOrgSettings(settings);
    } catch (error) {
      console.error('Error loading organization settings:', error);
      setOrgSettings({
        gradeSourcePreference: 'permission_based',
        allowLegacyFallback: false,
      });
    }
  };

  // Save organization settings
  const saveOrgSettings = async () => {
    if (!orgSettings) return;
    setSavingSettings(true);
    try {
      await updateOrganizationSettings(memoizedOrgId, {
        gradeSourcePreference: orgSettings.gradeSourcePreference,
        allowLegacyFallback: orgSettings.allowLegacyFallback,
      });
      setShowOrgSettings(false);
      // Refetch grades with new settings
      const gradesResponse = await getOrganizationActiveGrades(memoizedOrgId);
      if (Array.isArray(gradesResponse) && gradesResponse.length > 0) {
        const formattedGrades = gradesResponse.map((grade) => ({
          code: grade,
          name: grade,
        }));
        setActiveGrades(formattedGrades);
      }
      setToast({ show: true, variant: 'success', message: 'Settings saved successfully' });
    } catch (error) {
      console.error('Error saving organization settings:', error);
      setToast({ show: true, variant: 'danger', message: 'Failed to save settings' });
    } finally {
      setSavingSettings(false);
    }
  };

  // Refetch grades when legacy toggle changes
  useEffect(() => {
    const refetchGrades = async () => {
      if (!memoizedOrgId) return;
      try {
        const gradesResponse = useLegacyGrades
          ? await getActiveGradesLegacy(memoizedOrgId)
          : await getOrganizationActiveGrades(memoizedOrgId);
        
        if (Array.isArray(gradesResponse) && gradesResponse.length > 0) {
          const formattedGrades = gradesResponse.map((grade) => ({
            code: grade,
            name: grade,
          }));
          setActiveGrades(formattedGrades);
        }
      } catch (err) {
        console.error('Error refetching grades with toggle:', err);
      }
    };
    
    // Only refetch if we already have grades (don't run on initial mount)
    if (activeGrades.length > 0) {
      refetchGrades();
    }
  }, [useLegacyGrades, memoizedOrgId]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      if (!isSubscribed) return;

      setLoading(true);
      setError("");
      try {

        const fetchGrades = () =>
          useLegacyGrades
            ? getActiveGradesLegacy(memoizedOrgId)
            : getOrganizationActiveGrades(memoizedOrgId);

        let gradesResponse = await fetchGrades();
        const existingQuestionnaires = await getQuestionnairesByOrganization();

        if (!isSubscribed) return;
        let grades = Array.isArray(gradesResponse) ? gradesResponse : [];

        // If first request returned empty but grades exist (e.g. cache/backend timing), retry once after a short delay
        if ((!grades || grades.length === 0) && isSubscribed) {
          await new Promise((r) => setTimeout(r, 600));
          if (!isSubscribed) return;
          gradesResponse = await fetchGrades();
          grades = Array.isArray(gradesResponse) ? gradesResponse : [];
        }

        if (!grades || grades.length === 0) {
          console.warn("No active grades found for this organization");
          setError(
            "No active grades found for this organization. Please contact your administrator."
          );
          setLoading(false);
          return;
        }

        // Get drafts from local storage
        const drafts = JSON.parse(
          localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
        );

        // Filter drafts to only include those for the current organization
        const currentOrgDrafts = {};
        Object.entries(drafts).forEach(([id, draft]) => {
          // Include if no organizationId (for backward compatibility) or if it matches current org
          if (
            !draft.organizationId ||
            draft.organizationId.toString() === memoizedOrgId
          ) {
            currentOrgDrafts[id] = draft;
          }
        });

        // Create a map of API questionnaires for quick lookup
        const apiQuestionnairesMap = new Map(
          existingQuestionnaires.map((q) => [q.questionnaireId, q])
        );

        // Process drafts and merge with API questionnaires
        const mergedQuestionnaires = existingQuestionnaires.map((apiQ) => {
          const draft = currentOrgDrafts[apiQ.questionnaireId];
          if (draft) {
            // Calculate completion status
            const totalQuestions = Object.keys(QUESTIONS).length;
            const answeredQuestions = draft.questions
              ? Object.values(draft.questions).filter(
                  (q) => q && Object.keys(q).length > 0
                ).length
              : 0;
            const completionPercentage = Math.round(
              (answeredQuestions / totalQuestions) * 100
            );

            return {
              ...apiQ,
              ...draft,
              answers: mergeDraftAnswersWithApi(apiQ, draft),
              isDraft: true,
              hasLocalChanges: true,
              progress: completionPercentage,
              completedQuestions: answeredQuestions,
              totalQuestions: totalQuestions,
            };
          }
          return apiQ;
        });

        // Add any new drafts that don't exist in the API but belong to current org
        const newDrafts = Object.entries(currentOrgDrafts)
          .filter(([id]) => !apiQuestionnairesMap.has(id))
          .map(([id, draft]) => {
            // Calculate completion status
            const totalQuestions = Object.keys(QUESTION_NUMBERS).length;
            const answeredQuestions = draft.questions
              ? Object.values(draft.questions).filter(
                  (q) => q && Object.keys(q).length > 0
                ).length
              : 0;
            const completionPercentage = Math.round(
              (answeredQuestions / totalQuestions) * 100
            );

            return {
              ...draft,
              questionnaireId: id,
              isDraft: true,
              hasLocalChanges: true,
              organizationId: parseInt(memoizedOrgId), // Ensure they have the current org ID
              progress: completionPercentage,
              completedQuestions: answeredQuestions,
              totalQuestions: totalQuestions,
            };
          });

        // Format the grades array into objects with code and name
        // The GradeTable component expects { code, name } objects
        const formattedGrades = grades.map((grade) => ({
          code: grade, // Use the grade string as both code and name
          name: grade,
        }));

        if (!isSubscribed) return;

        setActiveGrades(formattedGrades);
        setQuestionnaires([...mergedQuestionnaires, ...newDrafts]);
      } catch (err) {
        if (!isSubscribed) return;

        console.error("Error in fetchData:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load data. Please check your connection and try again."
        );
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchData();

    loadQuestionnaires();

    return () => {
      isSubscribed = false;
    };
  }, [memoizedOrgId, useLegacyGrades]);

  useEffect(() => {
    const checkGrades = async () => {
      try {
        const gradesResponse = await (useLegacyGrades
          ? getActiveGradesLegacy(memoizedOrgId)
          : getOrganizationActiveGrades(memoizedOrgId));
        if (
          Array.isArray(gradesResponse) &&
          gradesResponse.length > 0 &&
          (!activeGrades || activeGrades.length === 0)
        ) {
          const formattedGrades = gradesResponse.map((grade) => ({
            code: grade,
            name: grade,
          }));
          setActiveGrades(formattedGrades);
          setError(""); // Clear "No active grades" error when grades load (e.g. after fallback or cache)
        }
      } catch (err) {
        console.error("Error in direct grades check:", err);
      }
    };

    checkGrades();
  }, [memoizedOrgId, activeGrades, useLegacyGrades]);

  useEffect(() => {
    const handleOrgIdChange = (event) => {
      const detail = event.detail;
      const newOrgId =
        typeof detail === "object" ? detail.orgId : detail;
      const source = typeof detail === "object" ? detail.source : null;

      if (source === "manual_refresh") {
      } else if (typeof detail === "object" && detail.previousOrgId) {
      }
      // Reset necessary state
      setActiveGrades([]);
      setQuestionnaires([]);
      setError("");
      setLoading(true);
      if (source === "manual_refresh") {
        handleSuccess("Refreshing questionnaire data...");
      }
      setTimeout(() => {
        loadQuestionnaires();
      }, 100);
    };

    window.addEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    return () => {
      window.removeEventListener(ORG_ID_CHANGE_EVENT, handleOrgIdChange);
    };
  }, []); // Empty dependency array - we'll handle loadQuestionnaires reference in a different way

  const handleCreateQuestionnaire = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const formattedDate = formatDate(now);
      const userId = localStorage.getItem("userId") || "mock-uid-" + Date.now();
      const questionnaireId = uuidv4();

      // Initialize answers with proper empty values
      const initialAnswers = {};

      QUESTION_NUMBERS.forEach((questionNumber) => {
        let answerValue = {};
        if (questionNumber === 4) {
          answerValue = {};
          activeGrades.forEach((grade) => {
            answerValue[grade.code] = [];
          });
        } else if (questionNumber === 14) {
          answerValue = {};
          activeGrades.forEach((grade) => {
            answerValue[grade.code] = {
              company: "",
              team: "",
              individual: "",
            };
          });
        } else if (questionNumber === 2 || questionNumber === 12) {
          answerValue = {};
          activeGrades.forEach((grade) => {
            answerValue[grade.code] = {
              basePay: "",
              tgp: "",
              other: "",
            };
          });
        } else if (questionNumber === 19) {
          answerValue = {};
          activeGrades.forEach((grade) => {
            answerValue[grade.code] = [];
          });
        } else {
          answerValue = { value: "" };
        }

        initialAnswers[String(questionNumber)] = {
          value: answerValue,
          metadata: {
            organizationId: memoizedOrgId,
            questionId: questionNumber,
            questionnaireId: questionnaireId,
          },
          timestamp: now.toISOString(),
          hasChanges: false,
          questionId: questionNumber,
          questionnaireId: questionnaireId,
        };
      });

      const orgIdNumber = parseInt(orgId);
      const initialFormData = {
        questionnaireId: questionnaireId,
        title: `Remuneration Survey - ${formattedDate}`,
        description: `Annual remuneration survey ID: ${questionnaireId
          .slice(0, 4)
          .toUpperCase()}`,
        organizationId: orgIdNumber,
        status: "DRAFT",
        answers: initialAnswers,
        createdBy: userId,
        updatedBy: userId,
      };

      const newQuestionnaire = await createQuestionnaireRaw(initialFormData);

      const drafts = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_KEY) || "{}"
      );
      drafts[newQuestionnaire.questionnaireId] = {
        ...newQuestionnaire,
        status: "DRAFT",
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(drafts));

      setDraftId(newQuestionnaire.questionnaireId);
      setFormData(newQuestionnaire);
      setAnswers(newQuestionnaire.answers);
      setCurrentStep(0);
      setShowEditorModal(true); // OPEN MODAL
      setCurrentQuestionnaire(newQuestionnaire);
      setModalMode("create");
      setUnsavedChanges(false);
      setChangeHistory({});

      // Add to questionnaires list
      setQuestionnaires((prev) => [...prev, newQuestionnaire]);

      handleSuccess("Questionnaire created successfully!");
    } catch (err) {
      console.error("Error creating questionnaire:", err);
      handleError(
        err.response?.data?.message?.join(", ") ||
          err.message ||
          "Failed to create questionnaire"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      setLoading(true);
      setError("");
      const localQuestionnaire = questionnaires.find(
        (q) => q.questionnaireId === id || q.id === id
      );
      let questionnaire;
      try {
        questionnaire = await getQuestionnaireById(id);
        if (Array.isArray(questionnaire.answers)) {
          questionnaire = {
            ...questionnaire,
            answers: normalizeAnswers(questionnaire.answers),
          };
        }
      } catch (fetchErr) {
        if (localQuestionnaire) {
          questionnaire = { ...localQuestionnaire };
          if (Array.isArray(questionnaire.answers)) {
            questionnaire = {
              ...questionnaire,
              answers: normalizeAnswers(questionnaire.answers),
            };
          }
        } else {
          throw fetchErr;
        }
      }
      const savedData = loadFromLocalStorage(id);
      if (savedData) {
        questionnaire = {
          ...questionnaire,
          ...savedData,
          answers: mergeDraftAnswersWithApi(questionnaire, savedData),
          changeHistory:
            savedData.changeHistory ?? questionnaire.changeHistory ?? {},
        };
      }
      setCurrentQuestionnaire(questionnaire);
      setFormData(questionnaire);
      setAnswers(questionnaire.answers || {});
      setChangeHistory(questionnaire.changeHistory || {});
      setDraftId(id);
      setCurrentStep(0);
      setShowEditorModal(true); 
      setModalMode("edit");
      setUnsavedChanges(false);
      if (questionnaire.answers) {
        saveToLocalStorage(id, questionnaire, orgId);
      }
    } catch (err) {
      console.error("Error editing questionnaire:", err);
      handleError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load questionnaire for editing"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (questionnaire) => {
    try {
      // Clear previous data immediately to prevent showing stale data
      setViewQuestionnaireData(null);
      setShowViewModal(false);
      setLoadingViewData(true);
      setError("");
      
      // Get the correct questionnaire ID - prioritize questionnaireId, then id
      const questionnaireId = questionnaire.questionnaireId || questionnaire.id;
      console.log('Viewing questionnaire with ID:', questionnaireId);
      console.log('Questionnaire object:', questionnaire);
      
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is missing');
      }
      
      console.log('Fetching questionnaire from API for view:', questionnaireId);
      let questionnaireData = await getQuestionnaireById(questionnaireId);
      console.log('Fetched questionnaire from API:', questionnaireData?.questionnaireId || questionnaireData?.id);

      // Verify we got the correct questionnaire
      const fetchedId = questionnaireData?.questionnaireId || questionnaireData?.id;
      if (fetchedId !== questionnaireId) {
        console.error('ID mismatch! Requested:', questionnaireId, 'Got:', fetchedId);
        throw new Error(`Questionnaire ID mismatch: requested ${questionnaireId}, got ${fetchedId}`);
      }
      
      // Normalize answers if needed
      if (Array.isArray(questionnaireData.answers)) {
        questionnaireData.answers = normalizeAnswers(questionnaireData.answers);
      }
      
      // Set new data and show modal
      setViewQuestionnaireData(questionnaireData);
      setShowViewModal(true);
    } catch (err) {
      console.error("Error loading questionnaire for viewing:", err);
      handleError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load questionnaire data"
      );
      // Ensure modal is closed on error
      setShowViewModal(false);
      setViewQuestionnaireData(null);
    } finally {
      setLoadingViewData(false);
    }
  };

  const handleDelete = (questionnaire) => {
    setConfirmData(questionnaire);
    setConfirmAction("delete");
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (confirmAction === "delete" && confirmData) {
      try {
        setLoading(true);
        setError("");

        const questionnaireId = confirmData.questionnaireId || confirmData.id;

        try {
          await deleteQuestionnaire(questionnaireId);
        } catch (apiErr) {
          console.error("Error deleting from API:", apiErr);
          if (!confirmData.isDraft) {
            throw apiErr;
          }
        }

        clearLocalStorage(questionnaireId);

        // Update UI state
        setQuestionnaires((prev) =>
          prev.filter(
            (q) =>
              q.questionnaireId !== questionnaireId && q.id !== questionnaireId
          )
        );

        handleSuccess("Questionnaire draft deleted successfully");
      } catch (err) {
        console.error("Error deleting questionnaire draft:", err);
        handleError("Failed to delete questionnaire draft: " + err.message);
      } finally {
        setLoading(false);
        setShowConfirmModal(false);
        setConfirmAction(null);
        setConfirmData(null);
      }
    } else {
      setShowConfirmModal(false);
      setConfirmAction(null);
      setConfirmData(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const questionnaireId = draftId || uuidv4();
      const draftData = {
        ...formData,
        answers,
        changeHistory,
        status: "DRAFT",
        organizationId: parseInt(memoizedOrgId),
        updatedAt: new Date().toISOString(),
        updatedBy: localStorage.getItem("userId") || "anonymous",
      };


      const response = await updateQuestionnaireRaw(questionnaireId, draftData);

      saveToLocalStorage(questionnaireId, draftData, memoizedOrgId);

      const normalizedAnswers = normalizeAnswersArray(answers);
      const completedQuestions = QUESTION_NUMBERS.filter((questionNumber) =>
        isQuestionEngaged(
          questionNumber,
          normalizedAnswers[questionNumber],
          normalizedAnswers
        )
      ).length;

      const progress = Math.round(
        (completedQuestions / QUESTION_NUMBERS.length) * 100
      );
      setProgress(progress);
      if (!draftId) {
        setDraftId(questionnaireId);
      }
      setQuestionnaires((prev) => {
        const existingIndex = prev.findIndex(
          (q) => q.questionnaireId === questionnaireId
        );

        const updatedDraft = {
          ...draftData,
          questionnaireId: questionnaireId,
          isDraft: true,
          hasLocalChanges: true,
          organizationId: parseInt(memoizedOrgId),
          progress: progress,
          completedQuestions: completedQuestions,
          totalQuestions: QUESTION_NUMBERS.length,
          lastSaved: new Date().toISOString(),
        };

        if (existingIndex >= 0) {
          const updatedList = [...prev];
          updatedList[existingIndex] = updatedDraft;
          return updatedList;
        } else {
          return [...prev, updatedDraft];
        }
      });

      setUnsavedChanges(false);
      setShowUnsavedModal(false);

      handleSuccess("Changes saved successfully to local storage");
    } catch (err) {
      console.error("Error saving questionnaire draft:", err);
      handleError(
        err.response?.data?.message ||
          err.message ||
          "Failed to save questionnaire draft"
      );
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async () => {
    setShowSubmitConfirm(false);
    try {
      setLoading(true);
      const questionnaireId = draftId || uuidv4();
      const submitData = {
        ...formData,
        answers,
        changeHistory,
        status:
          (await hasBaseGrades(memoizedOrgId)).hasBaseGrades === true
            ? "ACTIVE"
            : "ARCHIVED",
        organizationId: parseInt(memoizedOrgId),
        updatedAt: new Date().toISOString(),
        updatedBy: localStorage.getItem("userId") || "anonymous",
      };

      await updateQuestionnaireRaw(questionnaireId, submitData);

      setQuestionnaires((prev) => {
        const existingIndex = prev.findIndex(
          (q) => q.questionnaireId === questionnaireId
        );
        const updatedQuestionnaire = {
          ...submitData,
          questionnaireId: questionnaireId,
          isDraft: false,
          hasLocalChanges: false,
          organizationId: parseInt(memoizedOrgId),
          progress: 100,
          completedQuestions: Object.keys(QUESTIONS).length,
          totalQuestions: Object.keys(QUESTIONS).length,
          lastSaved: new Date().toISOString(),
        };
        if (existingIndex >= 0) {
          const updatedList = [...prev];
          updatedList[existingIndex] = updatedQuestionnaire;
          return updatedList;
        } else {
          return [...prev, updatedQuestionnaire];
        }
      });

      clearLocalStorage(questionnaireId);
      setShowEditorModal(false);
      handleSuccess("Questionnaire draft submitted successfully");
    } catch (err) {
      console.error("Error submitting questionnaire draft:", err);
      handleError(
        err.response?.data?.message ||
          err.message ||
          "Failed to submit questionnaire draft"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value, additionalData = null) => {
    const timestamp = new Date().toISOString();
    const previousAnswer = answers[questionId];
    const newAnswer = {
      value,
      metadata: {
        organizationId: memoizedOrgId,
        ...(additionalData && additionalData.details ? {} : additionalData),
      },
      timestamp,
      hasChanges: true,
      questionId,
      questionnaireId: draftId,
    };

    // Handle details property separately to ensure it's preserved correctly
    if (additionalData && additionalData.details) {
      newAnswer.details = additionalData.details;
    }



    setAnswers((prev) => ({
      ...prev,
      [String(questionId)]: newAnswer,
    }));
    setChangeHistory((prev) => ({
      ...prev,
      [String(questionId)]: {
        timestamp,
        previousValue: previousAnswer?.value || null,
        currentValue: newAnswer.value,
      },
    }));
    setUnsavedChanges(true);
  };

  const handleBack = () => {
    if (unsavedChanges) {
      setConfirmModalTitle("Unsaved Changes");
      setConfirmModalMessage(
        "You have unsaved changes. Would you like to save before going back?"
      );
      setShowConfirmModal(true);
      setConfirmAction(() => async () => {
        try {
          await handleSave();
          setActiveTab("questionnaires");
          setShowConfirmModal(false);
        } catch (err) {
          console.error("Error saving before navigation:", err);
          setConfirmModalTitle("Save Failed");
          setConfirmModalMessage(
            "Failed to save changes. Would you like to discard changes and go back anyway?"
          );
          setConfirmAction(() => () => {
            setActiveTab("questionnaires");
            setShowConfirmModal(false);
            setUnsavedChanges(false);
          });
        }
      });
    } else {
      setActiveTab("questionnaires");
    }
  };

  const handleNext = () => {
    if (formRef.current && !formRef.current.reportValidity()) {
      return;
    }
    if (currentStep < Object.keys(QUESTION_NUMBERS).length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleConfirmation = (action, data, title, message) => {
    setConfirmAction(() => action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };

  const handleError = (message) => {
    setToast({
      show: true,
      variant: "danger",
      message,
    });
    setError("");
  };

  const handleSuccess = (message) => {
    setToast({
      show: true,
      variant: "success",
      message,
    });
    setSuccess("");
  };

  const renderQuestionView = () => {

    if (!activeGrades || activeGrades.length === 0) {
      return (
        <div className="alert alert-warning">
          <h4>No Active Grades Available</h4>
          <p>
            There are no active grades configured for this organization. Please
            contact your administrator to set up grades before creating a
            questionnaire.
          </p>
          <div className="mt-3">
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  setLoading(true);
                  const gradesResponse = await getOrganizationActiveGrades(
                    memoizedOrgId
                  );

                  if (
                    Array.isArray(gradesResponse) &&
                    gradesResponse.length > 0
                  ) {
                    const formattedGrades = gradesResponse.map((grade) => ({
                      code: grade,
                      name: grade,
                    }));
                    setActiveGrades(formattedGrades);
                    setError("");
                  } else {
                    setError(
                      "No active grades found even after retry. Please contact your administrator."
                    );
                  }
                } catch (err) {
                  console.error("Error retrying grades fetch:", err);
                  setError("Failed to fetch active grades. Please try again.");
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              {loading ? "Retrying..." : "Retry Loading Grades"}
            </Button>
            <div className="mt-2 small text-muted">
              Organization ID: {memoizedOrgId}
            </div>
          </div>
        </div>
      );
    }

    if (loading && !formData) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (!formData) {
      return null;
    }

    return (
      <div className="questionnaire-container" style={{ padding: "1.5rem", height: "100%" }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div></div>
        </div>

        {error && (
          <ToastMessage
            show={toast.show}
            variant={toast.variant}
            message={toast.message}
            onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          />
        )}

        {success && (
          <ToastMessage
            show={toast.show}
            variant={toast.variant}
            message={toast.message}
            onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          />
        )}

        <Row className="g-0" style={{ height: "calc(100vh - 200px)" }}>
          <Col md={3} style={{ 
            borderRight: "1px solid #dee2e6",
            paddingRight: "1.5rem",
            paddingTop: "0.5rem"
          }}>
            <div style={{ 
              position: "sticky",
              top: "1rem",
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto"
            }}>
              <QuestionNav
                currentQuestion={currentStep}
                onQuestionClick={setCurrentStep}
                answers={answers}
              />
            </div>
          </Col>
          <Col md={9} style={{ paddingLeft: "1.5rem" }}>
            <Card className="shadow-sm">
              <Card.Body>
                <Form ref={formRef} onSubmit={handleSubmitClick}>
                  {renderQuestion(
                    currentStep + 1,
                    {
                      grades: activeGrades,
                    },
                    answers,
                    handleAnswerChange,
                    cleanupDeselectedPlans
                  )}
                  <NavigationButtons
                    currentStep={currentStep}
                    totalSteps={Object.keys(QUESTION_NUMBERS).length}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    onSave={handleSave}
                    onSubmit={handleSubmit}
                    onSubmitClick={handleSubmitClick}
                    loading={loading}
                    hasUnsavedChanges={unsavedChanges}
                    showSubmit={true}
                    answers={answers}
                  />
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <FloatingSaveReminder show={unsavedChanges} onSave={handleSave} />
      </div>
    );
  };


  const resetEditorState = () => {
    setFormData({ title: "", description: "", status: "draft", questions: [] });
    setCurrentQuestionnaire(null);
    setDraftId(null);
    setAnswers({});
    setChangeHistory({});
    setCurrentStep(0);
    setUnsavedChanges(false);
    setModalMode("create");
  };

  // Validate all required questions are answered
  const validateAllQuestions = () => {
    const questionList = Object.values(QUESTIONS)
      .filter(q => q.display !== false)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    
    const missingQuestions = [];
    
    questionList.forEach((question) => {
      const questionId = question.id;
      const answer = answers[questionId];
      
      if (!isQuestionEngaged(questionId, answer, answers)) {
        missingQuestions.push({
          id: questionId,
          title: question.title,
          displayOrder: question.displayOrder
        });
      }
    });
    
    return missingQuestions;
  };

  const handleSubmitClick = (e) => {
    e.preventDefault(); // Prevent default form submission
    if (formRef.current && !formRef.current.reportValidity()) {
      return;
    }
    
    // Validate all questions are answered
    // COMMENTED OUT: Validation disabled - not all checkboxes need to be ticked
    /*
    const missingQuestions = validateAllQuestions();
    
    if (missingQuestions.length > 0) {
      const missingList = missingQuestions
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(q => `${q.displayOrder}. ${q.title}`)
        .join('<br/>');
      
      const errorMessage = `Please answer all questions before submitting.<br/><br/><strong>Missing questions:</strong><br/>${missingList}`;
      
      handleError(errorMessage);
      
      // Also scroll to first missing question
      if (missingQuestions.length > 0) {
        const firstMissing = missingQuestions[0];
        const questionList = Object.values(QUESTIONS)
          .filter(q => q.display !== false)
          .sort((a, b) => a.displayOrder - b.displayOrder);
        const questionIndex = questionList.findIndex(q => q.id === firstMissing.id);
        if (questionIndex >= 0) {
          setCurrentStep(questionIndex);
        }
      }
      
      return;
    }
    */
    
    setShowSubmitConfirm(true);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Questionnaire Management</h2>
        <div className="d-flex gap-2 align-items-center">
          {/* KAM-only settings button */}
          {isKAMUser && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                loadOrgSettings();
                setShowOrgSettings(true);
              }}
              title="Configure grade source settings for this organization"
            >
              <i className="bi bi-gear me-1"></i>
              Grade Settings
            </Button>
          )}
          <Button
            variant="outline-secondary"
            onClick={loadQuestionnaires}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </Button>
          <Button
            variant="success"
            onClick={handleCreateQuestionnaire}
            disabled={!activeGrades || activeGrades.length === 0 || loading}
          >
            Create New Questionnaire
          </Button>
        </div>
      </div>

      {loading && !formData ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error && !error.includes("No active grades") ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          {error && error.includes("No active grades") && (
            <Alert variant="warning" className="mb-3">
              {error}
              <span className="ms-2 small">Checking again…</span>
            </Alert>
          )}
          {questionnaires.length === 0 ? (
        <Alert variant="info">
          No questionnaires found. Click the "Create New Questionnaire" button
          to create one.
        </Alert>
      ) : (
        <DataTable
          title="Questionnaires"
          data={questionnaires}
          collapsible={false}
          columns={[
            {
              key: 'title',
              header: 'Title',
              accessor: (row) => row.title || '-'
            },
            {
              key: 'description',
              header: 'Description',
              accessor: (row) => row.description || '-'
            },
            {
              key: 'status',
              header: 'Status',
              render: (value, row) => (
                <div className="d-flex flex-column gap-1">
                  <Badge
                    bg={
                      row.status === "DRAFT" ||
                      row.status === "draft"
                        ? "warning"
                        : row.status === "ACTIVE" ||
                          row.status === "active"
                        ? "success"
                        : row.status === "ARCHIVED" ||
                          row.status === "archived"
                        ? "secondary"
                        : "danger"
                    }
                  >
                    {row.status?.toUpperCase() || "DRAFT"}
                  </Badge>

                  {row.status === "ACTIVE" ||
                  row.status === "active" ? (
                    questionnaires.filter(
                      (q) =>
                        (q.status === "ACTIVE" || q.status === "active") &&
                        new Date(q.updatedAt || q.lastSaved || q.createdAt) >
                          new Date(
                            row.updatedAt ||
                              row.lastSaved ||
                              row.createdAt
                          )
                    ).length === 0 ? (
                      <Badge
                        bg="dark"
                        className="mt-1"
                        style={{ fontSize: "0.8rem" }}
                      >
                        Data Considered
                      </Badge>
                    ) : null
                  ) : null}

                  {row.hasLocalChanges && (
                    <Badge
                      bg="info"
                      className="mt-1"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Local Changes
                    </Badge>
                  )}
                </div>
              )
            },
            {
              key: 'lastUpdated',
              header: 'Last Updated',
              accessor: (row) => {
                if (row.lastSaved) {
                  return new Date(row.lastSaved).toLocaleString();
                } else if (row.updatedAt) {
                  return new Date(row.updatedAt).toLocaleString();
                } else if (row.createdAt) {
                  return new Date(row.createdAt).toLocaleString();
                }
                return "—";
              }
            },
            {
              key: 'actions',
              header: 'Actions',
              render: (value, row) => (
                <div>
                  <Button
                    size="sm"
                    variant="success"
                    className="me-2"
                    onClick={() => handleView(row)}
                    disabled={loadingViewData}
                  >
                    View
                  </Button>
                  {row.status !== "ACTIVE" &&
                    row.status !== "active" && (
                      <Button
                        size="sm"
                        variant="primary"
                        className="me-2"
                        onClick={() =>
                          handleEdit(
                            row.questionnaireId || row.id
                          )
                        }
                      >
                        Edit
                      </Button>
                    )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDelete(row)}
                  >
                    Delete
                  </Button>
                </div>
              )
            }
          ]}
          defaultOpen={true}
          searchable={true}
          pageSize={10}
        />
      )}
        </>
      )}

      {/* View Modal */}
      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewQuestionnaireData(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {viewQuestionnaireData?.title || "View Questionnaire"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {loadingViewData ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3 text-muted">Loading questionnaire data...</p>
            </div>
          ) : viewQuestionnaireData ? (
            <div>
              {viewQuestionnaireData.description && (
                <div className="mb-3">
                  <strong>Description:</strong>
                  <p>{viewQuestionnaireData.description}</p>
                </div>
              )}
              {viewQuestionnaireData.status && (
                <div className="mb-3">
                  <strong>Status:</strong>
                  <Badge
                    bg={
                      viewQuestionnaireData.status === "DRAFT" ||
                      viewQuestionnaireData.status === "draft"
                        ? "warning"
                        : viewQuestionnaireData.status === "ACTIVE" ||
                          viewQuestionnaireData.status === "active"
                        ? "success"
                        : viewQuestionnaireData.status === "ARCHIVED" ||
                          viewQuestionnaireData.status === "archived"
                        ? "secondary"
                        : "danger"
                    }
                    className="ms-2"
                  >
                    {viewQuestionnaireData.status.toUpperCase()}
                  </Badge>
                </div>
              )}
              <hr />
              <h5 className="mb-3">Questionnaire Answers</h5>
              {viewQuestionnaireData.answers && Object.keys(viewQuestionnaireData.answers).length > 0 ? (
                <div>
                  {Object.entries(QUESTIONS)
                    .filter(([key, question]) => question.display !== false)
                    .sort((a, b) => a[1].displayOrder - b[1].displayOrder)
                    .map(([key, question]) => {
                      const answer = viewQuestionnaireData.answers[question.id];
                      if (!answer || !answer.value) return null;
                      
                      return (
                        <Card key={question.id} className="mb-3">
                          <Card.Header>
                            <strong>{question.title}</strong>
                          </Card.Header>
                          <Card.Body>
                            <p className="text-muted small mb-2">{question.description}</p>
                            <div>
                              {typeof answer.value === 'object' && answer.value !== null ? (
                                Object.keys(answer.value).length > 0 ? (
                                  <Table bordered size="sm">
                                    <thead>
                                      <tr>
                                        <th>Grade</th>
                                        <th>Answer</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(answer.value).map(([grade, value]) => (
                                        <tr key={grade}>
                                          <td style={{ verticalAlign: 'top', fontWeight: '500' }}>{getAnswerRowLabel(grade)}</td>
                                          <td style={{ verticalAlign: 'top' }}>
                                            <FriendlyAnswerDisplay value={value} questionOptions={question.options} />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                ) : (
                                  <p className="text-muted mb-0">No data provided</p>
                                )
                              ) : (
                                <FriendlyAnswerDisplay value={answer.value} questionOptions={question.options} />
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    })}
                </div>
              ) : (
                <Alert variant="info">No answers found for this questionnaire.</Alert>
              )}
            </div>
          ) : (
            <Alert variant="warning">No questionnaire data available.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowViewModal(false);
            setViewQuestionnaireData(null);
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Editor Modal */}
      <Modal
        show={showEditorModal}
        onHide={() => {
          setShowEditorModal(false);
          resetEditorState();
        }}
        fullscreen
        backdrop="static"
        className="modal-fullscreen"
        style={{ padding: 0 }}
      >
        <Modal.Header
          closeButton={false}
          className="border-bottom shadow-sm bg-white sticky-top"
          style={{ padding: "1rem" }}
        >
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title>{formData?.title || "New Questionnaire"}</Modal.Title>
            <Button
              style={{
                color: "white",
                backgroundColor: REMCHANNEL_COLORS.steelBlue,
                borderColor: REMCHANNEL_COLORS.steelBlue,
              }}
              size="sm"
              onClick={() => setShowEditorModal(false)}
            >
              <i className="bi bi-x-lg me-1"></i>
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ 
          height: "calc(100vh - 60px)", 
          overflowY: "auto",
          padding: "1.5rem"
        }}>
          {renderQuestionView()}
        </Modal.Body>
      </Modal>

      <ToastMessage
        show={toast.show}
        variant={toast.variant}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />

      <ConfirmModal
        show={showConfirmModal}
        title={
          confirmModalTitle ||
          (confirmAction === "delete"
            ? "Delete Questionnaire"
            : "Confirm Action")
        }
        message={
          confirmModalMessage ||
          (confirmAction === "delete"
            ? `Are you sure you want to delete "${confirmData?.title}"?`
            : "Are you sure you want to proceed?")
        }
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
      />

      {/* Submit Confirmation Modal - always rendered at root */}
      <Modal
        show={showSubmitConfirm}
        onHide={() => setShowSubmitConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Submit Questionnaire</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to submit this questionnaire? You will not be
          able to edit it after submission.
          <br />
          <br />
          <p>
            <strong style={{ color: "red" }}>Note:</strong> This action will
            save the questionnaire and submit it to the server.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSubmitConfirm(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Confirm Submit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Organization Grade Settings Modal - KAM only */}
      <Modal
        show={showOrgSettings}
        onHide={() => setShowOrgSettings(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Grade Source Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {orgSettings ? (
            <>
              <p className="text-muted small mb-3">
                Configure how grades are determined for this organization.
              </p>
              
              <Form.Group className="mb-3">
                <Form.Label><strong>Grade Source Preference</strong></Form.Label>
                <Form.Select
                  value={orgSettings.gradeSourcePreference}
                  onChange={(e) => setOrgSettings({
                    ...orgSettings,
                    gradeSourcePreference: e.target.value
                  })}
                >
                  <option value="auto">Auto (recommended)</option>
                  <option value="permission_based">Permission-based only</option>
                  <option value="employee_based">Employee-based only (legacy)</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  <strong>Auto:</strong> Uses permission-based grades, falls back to employee-based if none found.<br/>
                  <strong>Permission-based:</strong> Only shows grades where IsGranted=1 in permissions.<br/>
                  <strong>Employee-based:</strong> Only shows grades with current employees (legacy behavior).
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="allow-legacy-fallback"
                  label="Allow fallback to employee-based grades"
                  checked={orgSettings.allowLegacyFallback}
                  onChange={(e) => setOrgSettings({
                    ...orgSettings,
                    allowLegacyFallback: e.target.checked
                  })}
                  disabled={orgSettings.gradeSourcePreference !== 'auto'}
                />
                <Form.Text className="text-muted">
                  When enabled (and using Auto mode), if no permission-based grades are found, 
                  the system will fall back to showing grades with current employees.
                </Form.Text>
              </Form.Group>

              <Alert variant="info" className="small">
                <strong>Note:</strong> Changes will take effect immediately and clear the grades cache for this organization.
              </Alert>
            </>
          ) : (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Loading settings...
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowOrgSettings(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={saveOrgSettings}
            disabled={savingSettings || !orgSettings}
          >
            {savingSettings ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Questionnaire;
